// A UI do Agzos Office: login, fluxo de eventos e painéis.
//
// O estado vem em duas mãos: o snapshot inicial (GET /api/snapshot) e depois os
// eventos do SSE (GET /api/events), que são aplicados no estado local.
//
// Layout (como um escritório de jogo): barra de cima com os números do dia, lateral
// com os projetos e seus agentes (subagentes aninhados), o campus no meio, o feed de
// atividade embaixo e o detalhe do agente selecionado à direita.
//
// Textos vêm de i18n.ts (pt, en, es). Quem redesenha o HTML dinâmico usa t(); os
// elementos fixos do index.html usam data-i18n (ver translateDom).

import { CAST_BY_ID, castFor } from '../core/cast.js';
import type { Look } from '../core/look.js';
import { nomesEmUso } from '../core/look-edit.js';
import type { OfficeSettings } from '../core/settings.js';
import type { Activity, Agent, OfficeEvent, OfficeSnapshot, PermissionQuestion, PermissionRequest, Provider } from '../core/types.js';
import { CampusRenderer } from './campus.js';
import { abreEditorPersonagem } from './editor.js';
import { characterFrame } from './engine/character.js';
import { mountHistory } from './history.js';
import { abreGuia, fechaGuia, guiaAberto, montaGuia, tituloGuia } from './guide.js';
import { type Chave, duracaoTurno, escolhaExplicita, getLang, iconeDoKind, type Lang, localeTag, onLangChange, plural, setLang, t, tActivity, tempoRelativo, translateDom } from './i18n.js';
import { githubFeedTexto, githubTexto } from './github-fx.js';
import { atualizaDia, montaDia } from './day.js';
import { aplicaDemo, atualizaDemo, montaDemo, seloPainel } from './demo.js';
import { LiveTerminal } from './live.js';
import { montaPtyJanela, PtyEstado, PtyTela, ptyPost } from './pty.js';
import { pastasSugeridas, sessaoDoAgente } from './pty-logica.js';
import { montaEntregas } from './deliveries.js';
import { montaTimelapse, timelapseAberto, timelapseAbre, timelapseFecha } from './timelapse.js';
import { PROVIDER_COLORS, PROVIDER_LABELS, PROVIDER_TAGS } from './sprites.js';
import { atualizaHud, ehCelular, filaDePedidos, montaMobile, vibraPedido } from './mobile.js';
import { estatisticasHtml, seloConsumoHtml } from './stats.js';
import { montaBarra, refreshTools } from './toolbar.js';
import { agentesComStatus, montaAtalhos, proximoDaFila, quemPrecisa } from './shortcuts.js';
import { montaPwa } from './pwa.js';
import { concluida, elenco, montaSons, painelSons, sino, sonsLigados } from './sound.js';
import { montaPainelRodas, rodaAtual, tracoDoAgente } from './world/rodas.js';
import { corpoDaDecisao, sugestaoDoBotao } from './aprovacao.js';
import { acompanhaMensagem, htmlDaSituacao, marcaEnvio, pintaSituacao } from './mensagem-situacao.js';
import './style.css';

interface State {
  snapshot: OfficeSnapshot | null;
  selectedAgent: string | null;
  /** Papel de quem entrou: membro só vê o escritório. */
  role: 'admin' | 'membro';
  connected: boolean;
  search: string;
  /** Providers escondidos pelo filtro. */
  hidden: Set<Provider>;
  /** Painel da direita: um agente, a Ponte ou o terminal (B23/B25). O 'Meu dia' é janela própria (day.ts). */
  detail: 'agent' | 'bridge' | 'terminal' | null;
  feedOpen: boolean;
}

const state: State = { snapshot: null, selectedAgent: null, role: 'admin', connected: false, search: '', hidden: new Set(), detail: null, feedOpen: true };

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`elemento não encontrado: ${sel}`);
  return el;
};

const loginScreen = $<HTMLElement>('#login');
const appScreen = $<HTMLElement>('#app');
const canvas = $<HTMLCanvasElement>('#office');
const numeros = $<HTMLElement>('#numeros');
const contas = $<HTMLElement>('#contas');
const conexao = $<HTMLElement>('#conexao');
const projetos = $<HTMLElement>('#projetos');
const filtros = $<HTMLElement>('#filtros');
const busca = $<HTMLInputElement>('#busca');
const btnPonte = $<HTMLButtonElement>('#btn-ponte');
const detalhe = $<HTMLElement>('#detalhe');
const feed = $<HTMLElement>('#feed');
const feedLista = $<HTMLElement>('#feed-lista');
const feedToggle = $<HTMLButtonElement>('#feed-toggle');
const toasts = $<HTMLElement>('#toasts');
const idioma = $<HTMLSelectElement>('#idioma');

let renderer: CampusRenderer | undefined;
/** Pedidos que já viraram aviso (não repetir a cada evento). */
const toasted = new Set<string>();

// ------------------------------------------------------------------ avisos e atalhos

/**
 * Textos desta parte (atalhos, avisos, botões novos). Ficam aqui e não no i18n.ts; o tipo
 * Record<Lang, ...> obriga pt, en e es a terem as mesmas chaves, como no dicionário.
 */
type Extra =
  | 'som'
  | 'som_titulo'
  | 'avisar'
  | 'avisar_titulo'
  | 'avisar_ligado'
  | 'avisar_bloqueado'
  | 'avisar_sem_suporte';

const EXTRA: Record<Lang, Record<Extra, string>> = {
  pt: {
    som: 'Som',
    som_titulo: 'Sons do escritório: sino, teclado, elevador e mais (desligados por padrão)',
    avisar: 'Avisar no navegador',
    avisar_titulo: 'Aviso do navegador quando alguém precisa de você',
    avisar_ligado: 'Avisos do navegador ligados (clique para desligar)',
    avisar_bloqueado: 'O navegador bloqueou os avisos. Libere nas configurações do site.',
    avisar_sem_suporte: 'Este navegador não tem avisos.',
  },
  en: {
    som: 'Sound',
    som_titulo: 'Office sounds: bell, keyboard, elevator and more (off by default)',
    avisar: 'Notify in browser',
    avisar_titulo: 'Browser notification when someone needs you',
    avisar_ligado: 'Browser notifications on (click to turn off)',
    avisar_bloqueado: 'The browser blocked notifications. Allow them in the site settings.',
    avisar_sem_suporte: 'This browser has no notifications.',
  },
  es: {
    som: 'Sonido',
    som_titulo: 'Sonidos de la oficina: campana, teclado, ascensor y más (apagados por defecto)',
    avisar: 'Avisar en el navegador',
    avisar_titulo: 'Aviso del navegador cuando alguien necesita tu ayuda',
    avisar_ligado: 'Avisos del navegador activos (clic para desactivar)',
    avisar_bloqueado: 'El navegador bloqueó los avisos. Permítelos en la configuración del sitio.',
    avisar_sem_suporte: 'Este navegador no tiene avisos.',
  },
  fr: {
    som: 'Son',
    som_titulo: 'Sons du bureau : cloche, clavier, ascenseur et plus (désactivés par défaut)',
    avisar: 'Alerter dans le navigateur',
    avisar_titulo: "Alerte du navigateur quand quelqu'un a besoin de vous",
    avisar_ligado: 'Alertes du navigateur activées (cliquer pour désactiver)',
    avisar_bloqueado: 'Le navigateur a bloqué les alertes. Autorisez-les dans les réglages du site.',
    avisar_sem_suporte: "Ce navigateur n'a pas d'alertes.",
  },
};

/** Preferências de aviso, desligadas por padrão. Sem localStorage (modo privado), valem só nesta aba. */
const PREFERENCIAS = { navegador: 'agzos.avisos.navegador' } as const;

function lePreferencia(chave: keyof typeof PREFERENCIAS): boolean {
  try {
    return globalThis.localStorage?.getItem(PREFERENCIAS[chave]) === '1';
  } catch {
    return false;
  }
}

function guardaPreferencia(chave: keyof typeof PREFERENCIAS, ligada: boolean): void {
  try {
    globalThis.localStorage?.setItem(PREFERENCIAS[chave], ligada ? '1' : '0');
  } catch {
    /* sem persistência: vale só nesta aba */
  }
}

let avisoNavegador = lePreferencia('navegador');
/** Agentes que já estavam 'precisa de você': o aviso dispara só na passagem para esse estado. */
const precisam = new Set<string>();

// os controles ficam na lateral, abaixo dos filtros: a topbar já está cheia com as contas
const barraAvisos = document.createElement('div');
barraAvisos.className = 'avisos-barra';
filtros.after(barraAvisos);
const btnSom = criaBotao('btn-som');
const btnAvisar = criaBotao('btn-avisar');
const btnAjuda = criaBotao('btn-ajuda');
// painel de sons (5.2): abre pelo botão Som, logo abaixo da barra; as preferências ficam em sound.ts
const painelDeSons = painelSons({ aoMudar: () => pintaControles(), aoFechar: () => mostraSons(false) });
painelDeSons.el.id = 'sons-painel';
painelDeSons.el.hidden = true;
barraAvisos.after(painelDeSons.el);
btnSom.setAttribute('aria-controls', 'sons-painel');

function mostraSons(aberto: boolean): void {
  if (aberto) painelDeSons.atualiza();
  painelDeSons.el.hidden = !aberto;
  btnSom.setAttribute('aria-expanded', String(aberto));
  if (!aberto) btnSom.focus();
}

/** Botão da barra de avisos. O texto e o estado vêm de pintaControles(). */
function criaBotao(id: string): HTMLButtonElement {
  const botao = document.createElement('button');
  botao.id = id;
  barraAvisos.append(botao);
  return botao;
}

/** Alguém passou a precisar de você (status waiting ou pedido de aprovação): sino e aviso do navegador. */
function alertaPrecisa(agent: Agent): void {
  if (precisam.has(agent.id)) return;
  precisam.add(agent.id);
  sino();
  if (avisoNavegador) avisoNoNavegador(agent);
}

/** Aviso do sistema só quando a aba não está focada: com ela à vista, o toast já basta. */
function avisoNoNavegador(agent: Agent): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  if (document.hasFocus() && !document.hidden) return;
  try {
    const aviso = new Notification('Agzos Office', {
      body: t('aviso.msg', { nome: agent.name, texto: t('aviso.precisa'), projeto: agent.project }),
      tag: agent.id,
    });
    aviso.onclick = () => {
      window.focus();
      aviso.close();
    };
  } catch {
    /* alguns navegadores só mostram aviso por service worker: segue sem */
  }
}

/** Quem já precisa de você ao abrir a página (ou num snapshot) não dispara aviso. */
function marcaPrecisam(agents: Agent[]): void {
  precisam.clear();
  for (const agent of agents) if (agent.status === 'waiting') precisam.add(agent.id);
}

/** Textos e estados dos botões e da ajuda. Refeito ao trocar o idioma e quando um botão muda. */
function pintaControles(): void {
  const e = EXTRA[getLang()];
  const semSuporte = !('Notification' in window);
  const bloqueado = !semSuporte && Notification.permission === 'denied';
  btnSom.textContent = e.som;
  btnSom.title = e.som_titulo;
  btnSom.setAttribute('aria-pressed', String(sonsLigados()));
  btnSom.classList.toggle('ligado', sonsLigados());
  btnAvisar.textContent = e.avisar;
  btnAvisar.disabled = semSuporte;
  btnAvisar.title = semSuporte ? e.avisar_sem_suporte : bloqueado ? e.avisar_bloqueado : avisoNavegador ? e.avisar_ligado : e.avisar_titulo;
  btnAvisar.setAttribute('aria-pressed', String(avisoNavegador));
  btnAvisar.classList.toggle('ligado', avisoNavegador);
  btnAjuda.textContent = '?';
  btnAjuda.title = tituloGuia();
  btnAjuda.setAttribute('aria-label', tituloGuia());
  btnAjuda.setAttribute('aria-controls', 'guia');
  btnAjuda.setAttribute('aria-expanded', String(guiaAberto()));
}

function ajudaAberta(): boolean {
  return guiaAberto();
}

/** Abre ou fecha o guia 'Como ler o escritório' (guide.ts), que é a ajuda. */
function mostraAjuda(aberta: boolean): void {
  if (aberta) abreGuia();
  else fechaGuia();
  btnAjuda.setAttribute('aria-expanded', String(aberta));
}

btnSom.addEventListener('click', () => mostraSons(painelDeSons.el.hidden === true));

btnAvisar.addEventListener('click', async () => {
  if (avisoNavegador) {
    avisoNavegador = false;
  } else if ('Notification' in window) {
    // a permissão só é pedida aqui, no clique da pessoa
    const permissao = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
    avisoNavegador = permissao === 'granted';
  }
  guardaPreferencia('navegador', avisoNavegador);
  pintaControles();
});

btnAjuda.addEventListener('click', () => mostraAjuda(!ajudaAberta()));

pintaControles();
montaGuia();

// textos fixos (data-i18n) primeiro; o resto é desenhado pelo render()
translateDom();
idioma.value = getLang();
idioma.addEventListener('change', () => setLang(idioma.value as Lang));
onLangChange(() => {
  idioma.value = getLang();
  pintaControles();
  render();
});

// ------------------------------------------------------------------ login

$<HTMLFormElement>('#login-form').addEventListener('submit', async (event) => {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const data = new FormData(form);
  const error = $<HTMLElement>('#login-error');
  error.textContent = '';
  const body: { user?: string; password: string; totp?: string } = { password: String(data.get('password') ?? '') };
  const usuario = String(data.get('usuario') ?? '').trim();
  if (usuario) body.user = usuario;
  const totp = String(data.get('totp') ?? '').trim();
  if (totp) body.totp = totp;
  const response = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string; needsTotp?: boolean };
  if (json.ok) {
    await start();
    return;
  }
  error.textContent = json.needsTotp ? t('login.totp_pedido') : (json.error ?? t('erro.nao_deu'));
  if (json.needsTotp) $<HTMLInputElement>('#totp')?.focus();
});

$<HTMLButtonElement>('#logout').addEventListener('click', async () => {
  await fetch('/api/logout', { method: 'POST' });
  window.location.reload();
});

// ------------------------------------------------------------------ ciclo

async function start(): Promise<void> {
  loginScreen.hidden = true;
  appScreen.hidden = false;

  const session = await fetch('/api/session').catch(() => null);
  if (session && !session.ok) {
    loginScreen.hidden = false;
    appScreen.hidden = true;
    return;
  }
  // membro (convite só do escritório): o CSS esconde ajustes, ponte, terminal, mensagem e aprovar
  const quem = (await session?.json().catch(() => ({}))) as { role?: 'admin' | 'membro' } | undefined;
  state.role = quem?.role ?? 'admin';
  document.body.dataset.role = state.role;
  // modo demonstração (5.6): faixa do selo, convite com o escritório vazio e ?mock=1
  void montaDemo(state.role === 'admin');
  document.dispatchEvent(new CustomEvent('agzos:papel'));

  const snapshot = (await (await fetch('/api/snapshot')).json()) as OfficeSnapshot;
  state.snapshot = snapshot;
  marcaPrecisam(snapshot.agents);

  renderer = new CampusRenderer(canvas, {
    onSelect: (agentId) => selectAgent(agentId, false),
    // o botão Seguir do painel muda quando a câmera para de seguir (arrastar) ou troca de agente
    onFollow: () => renderDetail(),
  });
  renderer.applySnapshot(snapshot);
  renderer.start();
  // o teclado (5.2) toca só por quem está digitando nas salas da tela
  montaSons(() => renderer?.trabalhandoNaTela() ?? 0);
  montaPainelRodas();
  window.addEventListener('resize', () => renderer?.resize());
  // timelapse do dia (B21, 7.2): o player mostra os quadros no campus (timelapse.ts)
  montaTimelapse(() => renderer);
  montaBarra({
    campus: renderer,
    painel: () => state.detail,
    terminal: () => alternaTerminal(),
    timelapse: () => timelapseAbre(chaveDoDia(new Date())),
    timelapseAberto,
    ajudaAberta,
    ajuda: () => mostraAjuda(!ajudaAberta()),
  });
  // atalhos de tela (5.1): L, P, [, ], setas e WASD, 0 (shortcuts.ts)
  montaAtalhos({
    campus: renderer,
    timelapse: () => (timelapseAberto() ? timelapseFecha() : timelapseAbre(chaveDoDia(new Date()))),
    esperam: () => quemPrecisa(state.snapshot),
    agenteAtual: () => state.selectedAgent,
    selecionaAgente: (id) => selectAgent(id, true),
  });
  // celular (B29-M3): HUD, abas e bottom sheets só quando a tela é estreita
  montaMobile();
  // celular (B29-M4): o modal de pedido e pergunta; no desktop fica escondido
  missao = montaMissao();
  // notificação no celular (B29-M5): o link ?agente=… e o toque no aviso (mensagem do SW) abrem o agente
  const agenteDoLink = new URLSearchParams(location.search).get('agente');
  if (agenteDoLink) selectAgent(agenteDoLink, true);
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (evento) => {
      const dados = evento.data as { type?: string; id?: string | null } | null;
      if (dados?.type === 'agente' && dados.id) selectAgent(dados.id, true);
    });
  }

  connect();
  entregas.inicia();
  applyTitle();
  render();
}

function connect(): void {
  const source = new EventSource('/api/events');
  source.onopen = () => {
    state.connected = true;
    renderTop();
  };
  source.onerror = () => {
    state.connected = false;
    renderTop();
  };
  source.onmessage = (message) => {
    const event = JSON.parse(message.data) as OfficeEvent;
    applyEvent(event);
  };
}

function applyEvent(event: OfficeEvent): void {
  const snapshot = state.snapshot;
  if (!snapshot) return;
  switch (event.type) {
    case 'snapshot':
      state.snapshot = event.snapshot;
      renderer?.applySnapshot(event.snapshot);
      marcaPrecisam(event.snapshot.agents);
      break;
    case 'agents': {
      const gone = new Set([...(event.removed ?? []), ...event.agents.map((x) => x.id)]);
      const others = snapshot.agents.filter((a) => !gone.has(a.id));
      const depois = [...others, ...event.agents];
      state.snapshot = { ...snapshot, agents: depois, rooms: event.rooms };
      renderer?.applyEvent(event);
      // alguém entrou ou saiu do escritório: ding do elevador (5.2)
      elenco(snapshot.agents, depois);
      for (const id of event.removed ?? []) precisam.delete(id);
      for (const agent of event.agents) {
        if (agent.status !== 'waiting') {
          precisam.delete(agent.id);
          continue;
        }
        toast(agent, t('aviso.precisa'));
        alertaPrecisa(agent);
      }
      break;
    }
    case 'activity':
      state.snapshot = { ...snapshot, activities: [...snapshot.activities, event.activity].slice(-200) };
      renderer?.applyEvent(event);
      if (event.activity.kind === 'done') concluida();
      break;
    case 'permission': {
      state.snapshot = { ...snapshot, permissions: [...snapshot.permissions, event.request] };
      renderer?.applyEvent(event);
      // pedido novo no celular: vibra uma vez (o modal aparece sozinho no render)
      if (!vibrados.has(event.request.id)) {
        vibrados.add(event.request.id);
        if (ehCelular()) vibraPedido();
      }
      const agent = snapshot.agents.find((a) => a.id === event.request.agentId);
      if (agent) {
        toast(agent, event.request.title, event.request.id);
        alertaPrecisa(agent);
      }
      break;
    }
    case 'permission-resolved':
      state.snapshot = { ...snapshot, permissions: snapshot.permissions.filter((p) => p.id !== event.id) };
      renderer?.applyEvent(event);
      break;
    case 'providers':
      state.snapshot = { ...snapshot, providers: event.providers };
      break;
    case 'settings':
      state.snapshot = { ...snapshot, settings: event.settings };
      renderer?.applyEvent(event);
      applyTitle();
      break;
    case 'features':
      state.snapshot = { ...snapshot, features: event.features };
      break;
    case 'demo':
      aplicaDemo(event.ligado);
      atualizaDia();
      break;
    case 'social':
      state.snapshot = { ...snapshot, social: { ...snapshot.social, ...event.social } };
      renderer?.applyEvent(event);
      break;
    case 'delivery':
      entregas.recebe(event.delivery);
      break;
    case 'github': {
      // GitHub nos transcripts: a sala anima (campus) e quem fez o que ganha um aviso; só o ao vivo avisa
      renderer?.applyEvent(event);
      const quem = snapshot.agents.find((a) => a.id === event.agentId);
      if (quem && event.live) toast(quem, githubTexto(event.signal), `gh:${event.id}`);
      break;
    }
    default:
      break;
  }
  render();
}

// ------------------------------------------------------------------ render

function render(): void {
  renderTop();
  atualizaDemo(state.snapshot?.agents.length ?? 0, state.role === 'admin');
  renderSidebar();
  renderFeed();
  renderDetail();
  vivo.update();
  renderMissao();
}

function tagOf(provider: Provider): string {
  return `<span class="tag" style="background:${PROVIDER_COLORS[provider] ?? '#888'}">${PROVIDER_TAGS[provider] ?? '?'}</span>`;
}

function renderTop(): void {
  const snapshot = state.snapshot;
  conexao.className = `conexao ${state.connected ? 'on' : 'off'}`;
  conexao.textContent = state.connected ? t('topo.ao_vivo') : t('topo.reconectando');
  refreshTools();
  if (!snapshot) return;
  const agents = snapshot.agents;
  const waiting = new Set([...agents.filter((a) => a.status === 'waiting').map((a) => a.id), ...snapshot.permissions.map((p) => p.agentId)]).size;
  const chips: Array<[string, number, string]> = [
    [t('num.salas'), snapshot.rooms.length, ''],
    [t('num.agentes'), agents.length, ''],
    [t('num.trabalhando'), agents.filter((a) => a.status === 'working' || a.status === 'thinking').length, 'verde'],
    [t('num.subagentes'), agents.filter((a) => a.parentId).length, 'azul'],
    [t('num.precisam'), waiting, waiting ? 'alerta' : ''],
  ];
  // contador de shells (5.1): só aparece com algum; clicar leva ao próximo que está com shell rodando
  const shells = agentesComStatus(agents, 'shell').length;
  const botaoShells = shells ? `<button type="button" class="numero shell" data-shells title="${escape(t('num.shells_dica'))}"><b>${shells}</b><span>${t('num.shells')}</span></button>` : '';
  // role=status: leitores de tela anunciam quando o número muda (trocaHtml só mexe se mudou)
  trocaHtml(numeros, chips.map(([label, n, cls]) => `<div class="numero ${cls}" role="status"><b>${n}</b><span>${label}</span></div>`).join('') + botaoShells);
  trocaHtml(
    contas,
    snapshot.providers
      .filter((p) => p.sessions > 0 || p.error || p.usage)
      .map((p) => {
        const cli = snapshot.settings?.clis.find((c) => c.id === p.accountId);
        const label = escape(cli?.label ?? p.label ?? PROVIDER_LABELS[p.provider] ?? p.provider);
        const tag = cli?.tag || cli?.color ? `<span class="tag" style="background:${escape(cli.color ?? PROVIDER_COLORS[p.provider] ?? '#888')}">${escape(cli.tag ?? PROVIDER_TAGS[p.provider] ?? '?')}</span>` : tagOf(p.provider);
        const usage = p.usage?.windows.length ? `<div class="uso">${p.usage.windows.map(usageRow).join('')}</div>` : '';
        const sub = p.error ? t('conta.erro_leitura') : plural('conta.sessoes', p.sessions);
        return `<div class="conta" title="${escape(p.error ?? (p.usage ? `${p.usage.source} · ${hora(p.usage.fetchedAt)}` : ''))}">${tag}<div><b>${label}</b>${seloConsumoHtml(p.usage, Date.now())}${usage || `<small>${sub}</small>`}</div></div>`;
      })
      .join(''),
  );
  atualizaHud(snapshot, state.connected, waiting);
}

/** Uma barra de consumo: '5h ▮▮▮▯▯▯ 12% ↻4h33' (como o Habblaud). */
function usageRow(w: { label: string; percent: number; resetsAt?: number }): string {
  const pct = Math.max(0, Math.min(100, Math.round(w.percent)));
  const cls = pct >= 90 ? 'alto' : pct >= 70 ? 'medio' : 'baixo';
  const reset = w.resetsAt ? `<i>↻${tempoAte(w.resetsAt)}</i>` : '';
  return `<span class="uso-linha ${cls}"><em>${escape(w.label)}</em><span class="uso-barra" role="meter" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100" aria-label="${escape(w.label)}"><s style="width:${pct}%"></s></span><b>${pct}%</b>${reset}</span>`;
}

/** '4h33', '1d20h', '12min' até o instante. */
function tempoAte(at: number): string {
  const min = Math.max(0, Math.round((at - Date.now()) / 60_000));
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h${String(min % 60).padStart(2, '0')}`;
  return `${Math.floor(h / 24)}d${h % 24}h`;
}

/** Título e idioma padrão do CMS. O idioma só vale se a pessoa não escolheu um. */
function applyTitle(): void {
  const lang = state.snapshot?.settings?.theme.lang;
  if (lang && lang !== 'auto' && lang !== getLang() && !escolhaExplicita()) setLang(lang, false);
  const title = state.snapshot?.settings?.theme.title;
  if (!title) return;
  document.title = title;
  const h1 = document.querySelector('#topbar h1');
  if (h1) h1.textContent = title;
}

function visibleAgent(agent: Agent): boolean {
  if (state.hidden.has(agent.provider)) return false;
  const q = state.search.trim().toLowerCase();
  if (!q) return true;
  return [agent.name, agent.project, agent.cwd, agent.provider, agent.activity].some((v) => v.toLowerCase().includes(q));
}

function renderSidebar(): void {
  const snapshot = state.snapshot;
  if (!snapshot) return;
  const providers = [...new Set(snapshot.agents.map((a) => a.provider))].sort();
  trocaHtml(
    filtros,
    providers
      .map((p) => `<button class="filtro ${state.hidden.has(p) ? 'off' : ''}" data-provider="${p}" aria-pressed="${!state.hidden.has(p)}">${tagOf(p)}${escape(PROVIDER_LABELS[p] ?? p)}</button>`)
      .join(''),
  );

  const byId = new Map(snapshot.agents.map((a) => [a.id, a]));
  // projetos com alguém esperando sobem para o topo
  const rooms = [...snapshot.rooms].sort((a, b) => Number(b.agentIds.some((id) => byId.get(id)?.status === 'waiting')) - Number(a.agentIds.some((id) => byId.get(id)?.status === 'waiting')));
  const cards = rooms
    .map((room) => {
      const agents = room.agentIds.map((id) => byId.get(id)).filter((a): a is Agent => !!a && visibleAgent(a));
      if (!agents.length) return '';
      const mains = agents.filter((a) => !a.parentId || !agents.some((p) => p.id === a.parentId));
      const subsOf = (id: string) => agents.filter((a) => a.parentId === id);
      const busy = agents.filter((a) => a.status === 'working' || a.status === 'thinking').length;
      const waiting = agents.some((a) => a.status === 'waiting');
      const tags = [...new Set(agents.map((a) => a.provider))].map(tagOf).join('');
      const rows = mains
        .map((a) => {
          const subs = subsOf(a.id);
          if (!subs.length) return agentRow(a, false);
          // muitos subagentes: recolhidos por padrão (até 3 ficam abertos); a escolha fica lembrada
          const aberto = subsAbertos.get(a.id) ?? subs.length <= SUBS_ABERTOS_ATE;
          const ativos = subs.filter((x) => x.status === 'working' || x.status === 'thinking').length;
          const esperam = subs.some((x) => x.status === 'waiting');
          const resumo = `${plural('subs', subs.length)}${ativos ? ` · ${t('subs.trabalhando', { n: ativos })}` : ''}`;
          return `${agentRow(a, false)}<div class="subs ${aberto ? 'aberto' : 'fechado'}"><button class="subs-toggle ${esperam ? 'esperando' : ''}" data-subs="${escape(a.id)}" aria-expanded="${aberto}"><span aria-hidden="true">${aberto ? '▾' : '▸'}</span> ${resumo}</button>${aberto ? subs.map((x) => agentRow(x, true)).join('') : ''}</div>`;
        })
        .join('');
      return `<article class="projeto ${waiting ? 'esperando' : ''}">
        <header><div><b>${escape(room.project)}</b> <span class="cont">${agents.length}</span><small>${escape(shortPath(room.cwd))}</small></div><div class="tags">${tags}</div></header>
        <div class="barra" title="${escape(t('projeto.trabalhando', { n: busy, total: agents.length }))}"><i style="width:${Math.round((busy / agents.length) * 100)}%"></i></div>
        ${rows}
      </article>`;
    })
    .join('');
  trocaHtml(projetos, cards || `<p class="vazio">${t('lateral.vazio')}</p>`);
}

/** Subagentes abertos/fechados na lateral, por agente principal (lembrado neste navegador). */
const SUBS_ABERTOS_ATE = 3;
const subsAbertos = new Map<string, boolean>(
  (() => {
    try {
      return Object.entries(JSON.parse(localStorage.getItem('agzos.subs') ?? '{}') as Record<string, boolean>);
    } catch {
      return [];
    }
  })(),
);
projetos.addEventListener('click', (e) => {
  const botao = (e.target as HTMLElement).closest<HTMLElement>('[data-subs]');
  if (!botao?.dataset.subs) return;
  e.stopPropagation();
  const id = botao.dataset.subs;
  subsAbertos.set(id, botao.getAttribute('aria-expanded') !== 'true');
  try {
    // só os 200 mais recentes: o resto volta ao padrão
    localStorage.setItem('agzos.subs', JSON.stringify(Object.fromEntries([...subsAbertos].slice(-200))));
  } catch {
    // sem armazenamento: vale só nesta página
  }
  renderSidebar();
});

function agentRow(agent: Agent, sub: boolean): string {
  const selected = agent.id === state.selectedAgent ? 'sel' : '';
  return `<button class="agente status-${agent.status} ${sub ? 'sub' : ''} ${selected}" data-agent="${escape(agent.id)}">
    <img class="avatar" src="${avatarUrl(lookOf(agent))}" alt="" width="32" height="32" />
    <span class="info"><span class="linha1"><b>${escape(agent.name)}</b>${tagOf(agent.provider)}<em>${t(sub ? 'agente.sub' : 'agente.principal')}</em></span>
    <span class="linha2"><span class="atividade">${escape(tActivity(agent.activity) || statusLabel(agent.status))}</span>${cronometro(agent)}</span></span>
    <span class="ponto" role="img" title="${statusLabel(agent.status)}" aria-label="${statusLabel(agent.status)}"></span>
  </button>`;
}

function renderFeed(): void {
  const snapshot = state.snapshot;
  if (!snapshot) return;
  feed.classList.toggle('fechado', !state.feedOpen);
  feedToggle.setAttribute('aria-expanded', String(state.feedOpen));
  if (!state.feedOpen) return;
  const byId = new Map(snapshot.agents.map((a) => [a.id, a]));
  // só atividade de quem ainda está no escritório
  const items = snapshot.activities.filter((a) => byId.has(a.agentId)).slice(-30).reverse();
  trocaHtml(
    feedLista,
    items.length
      ? items
          .map((a) => {
            const agent = byId.get(a.agentId);
            const who = agent
              ? `<img class="avatar mini" src="${avatarUrl(lookOf(agent))}" alt="" width="20" height="20" /><b>${escape(agent.name)}</b><span class="proj">${escape(agent.project)}</span>`
              : '<span></span><b>—</b><span class="proj"></span>';
            return `<li data-agent="${escape(a.agentId)}" tabindex="0" class="kind-${a.kind}"><time>${hora(a.at, true)}</time>${who}<span class="texto">${escape(githubFeedTexto(a) ?? tActivity(a.text))}</span></li>`;
          })
          .join('')
      : `<li class="vazio">${t('feed.vazio')}</li>`,
  );
}

function renderDetail(): void {
  // a conversa só vive enquanto o painel mostra o mesmo agente
  if (conversa && (state.detail !== 'agent' || state.selectedAgent !== conversa.agentId)) fechaConversa();
  // o terminal atualiza sozinho a cada segundo; fora do painel, o timer para
  if (terminalTimer !== undefined && state.detail !== 'terminal') paraTerminal();
  const snapshot = state.snapshot;
  if (!snapshot || !state.detail) {
    detalhe.hidden = true;
    return;
  }
  // não refaz o painel enquanto a pessoa digita uma mensagem
  if (document.activeElement?.id === 'mensagem') return;
  detalhe.hidden = false;
  if (state.detail === 'terminal') {
    // a casca não muda com os eventos; a lista e a tela são atualizadas pelo timer
    trocaHtml(detalhe, terminalCasca());
    return;
  }
  if (state.detail === 'bridge') {
    trocaHtml(detalhe, bridgePanel(snapshot));
    return;
  }
  const agent = snapshot.agents.find((a) => a.id === state.selectedAgent);
  if (!agent) {
    fechaConversa();
    detalhe.hidden = true;
    return;
  }
  if (conversa) {
    pintaConversa(agent);
    return;
  }
  const html = agentePainel(agent, snapshot);
  const abertos = detalhesAbertos();
  if (!trocaHtml(detalhe, html)) return;
  restauraDetalhes(abertos);
  bindComposer(agent);
  bindConversa(agent);
}

// ------------------------------------------------------------------ painel do agente (P3)

/** Recado curto do painel (erro de mandar mensagem ou de assumir). Some sozinho depois de um tempo. */
let recadoAgente: string | undefined;
let recadoTimer: number | undefined;

/** Mostra (ou apaga, com undefined) o recado do painel sem refazer o HTML: vale mesmo com a caixa de mensagem em foco. */
function diz(texto: string | undefined): void {
  recadoAgente = texto;
  window.clearTimeout(recadoTimer);
  if (texto) recadoTimer = window.setTimeout(() => diz(undefined), 6000);
  const el = detalhe.querySelector<HTMLElement>('#recado-agente');
  if (!el) return renderDetail();
  el.textContent = texto ?? '';
  el.hidden = !texto;
}

/** Quem está aberto dentro do painel (Comando, Extrato), para não fechar a cada evento. */
function detalhesAbertos(): Set<string> {
  return new Set([...detalhe.querySelectorAll<HTMLDetailsElement>('details[data-chave][open]')].map((d) => d.dataset.chave ?? ''));
}

function restauraDetalhes(abertos: Set<string>): void {
  for (const d of detalhe.querySelectorAll<HTMLDetailsElement>('details[data-chave]')) d.open = abertos.has(d.dataset.chave ?? '');
}

/**
 * O painel do agente: identidade e conta, título da sessão, status com tempo, ações (seguir,
 * centralizar, terminal), o que está fazendo agora, mensagem, vida social, subagentes e linha do tempo.
 * Qualquer parte pode faltar (dado ainda não chegou): cada uma tem o seu vazio.
 */
function agentePainel(agent: Agent, snapshot: OfficeSnapshot): string {
  const member = CAST_BY_ID.get(agent.look) ?? castFor(agent.id);
  const parent = agent.parentId ? snapshot.agents.find((a) => a.id === agent.parentId) : undefined;
  const subs = snapshot.agents.filter((a) => a.parentId === agent.id);
  const permissions = snapshot.permissions.filter((p) => p.agentId === agent.id);
  const doAgente = snapshot.activities.filter((a) => a.agentId === agent.id);
  const recentes = doAgente.slice(-12).reverse();
  const sala = snapshot.rooms.find((r) => r.agentIds.includes(agent.id));
  const seguindo = renderer?.follows(agent.id) ?? false;
  const fired = Math.max(agent.subagentsFired ?? 0, subs.length);
  return `
    <header class="det-cab"><span>${t('det.agente')}</span><button class="fechar" data-close aria-label="${t('acao.fechar')}">✕</button></header>
    <div class="det-perfil">
      <img class="avatar grande" src="${avatarUrl(agent.appearance ?? member.look)}" alt="" width="64" height="64" />
      <div class="det-id">
        <h3>${escape(agent.name)}${
          !agent.parentId && state.role === 'admin' && !agent.id.startsWith('demo:')
            ? ` <button class="det-lapis" data-acao="editar-personagem" aria-label="${escape(t('det.editar_personagem'))}" title="${escape(t('det.editar_personagem'))}">✎</button>`
            : ''
        }</h3>${seloPainel(agent)}
        <small>${parent ? t('det.subagente_de', { nome: escape(parent.name) }) : t('det.agente_principal')} · ${escape(member.role)}</small>
        ${contaHtml(agent, snapshot)}
        <p class="det-sala">${t('det.sala')} ${
          sala
            ? `<button class="det-link" data-acao="sala" data-sala="${escape(sala.id)}" title="${escape(t('det.sala_centraliza', { nome: sala.project }))}">${escape(sala.project)}</button>`
            : `<b>${escape(agent.project)}</b>`
        }</p>
      </div>
    </div>
    <div class="det-titulo"><small>${t('det.sessao')}</small><b>${escape(agent.title || t('det.sem_titulo'))}</b></div>
    <div class="det-status status-${agent.status}"><span class="ponto"></span><b>${capitaliza(statusLabel(agent.status))}</b>${
      agent.statusSince ? ` <span class="det-ha" data-ha="${agent.statusSince}">${tempoRelativo(agent.statusSince)}</span>` : ''
    }</div>
    <div class="det-acoes-linha">
      <button data-acao="seguir" aria-pressed="${seguindo}" title="${escape(t('det.seguir_titulo'))}">${t('det.seguir')}</button>
      <button data-acao="centralizar" title="${escape(t('det.centralizar_titulo'))}">${t('det.centralizar')}</button>
    </div>
    <button class="det-terminal" data-acao="terminal" title="${escape(t('det.terminal_vivo_titulo'))}"><span>${t('det.terminal_vivo')}</span><kbd>T</kbd></button>
    <p id="recado-agente" class="erro" role="alert" ${recadoAgente ? '' : 'hidden'}>${escape(recadoAgente ?? '')}</p>
    ${permissions.map(permissionCard).join('')}
    <section class="det-agora"><h4>${t('det.agora')}</h4>${agoraHtml(agent, recentes[0])}</section>
    <section class="det-mandar"><h4>${t('det.mandar')}</h4>${composerHtml(agent, snapshot)}</section>
    ${socialHtml(agent, snapshot)}
    <section><h4>${t('det.subagentes')}</h4>${subs.length ? `<div class="subs-lista">${subs.map((s) => agentRow(s, true)).join('')}</div>` : ''}${
      fired ? `<p class="det-disparados">${t('det.disparados', { n: fired })}</p>` : `<p class="vazio">${t('det.nenhum_subagente')}</p>`
    }</section>
    <section class="det-linha"><h4>${t('det.linha')} <span class="det-contador">${doAgente.length}</span></h4><ol class="tempo">${
      recentes.map(linhaItem).join('') || `<li class="vazio">${t('det.sem_atividade')}</li>`
    }</ol></section>
    ${estatisticasHtml(agent)}
    <section class="det-itens">
      <button class="conversa-abrir" data-conversa>${t('conversa.abrir')}</button>
      ${assumirHtml(agent)}
    </section>
    ${agent.tokens ? `<p class="det-tokens">${t('det.tokens', { n: agent.tokens.toLocaleString(localeTag()) })}</p>` : ''}
    <p class="det-cwd">${escape(agent.cwd)}</p>`;
}

/** A conta de onde a sessão veio: letra, nome e e-mail (quando a CLI guarda). */
function contaHtml(agent: Agent, snapshot: OfficeSnapshot): string {
  const conta = snapshot.providers.find((p) => (agent.accountId ? p.accountId === agent.accountId : p.provider === agent.provider));
  const cli = snapshot.settings?.clis.find((c) => c.id === agent.accountId);
  const nome = cli?.label ?? conta?.label ?? PROVIDER_LABELS[agent.provider] ?? agent.provider;
  const letra = cli?.tag || PROVIDER_TAGS[agent.provider] || '?';
  const cor = cli?.color ?? PROVIDER_COLORS[agent.provider] ?? '#888';
  return `<p class="det-conta"><span class="tag" style="background:${escape(cor)}">${escape(letra)}</span><b>${escape(nome)}</b>${
    conta?.email ? `<small>${escape(conta.email)}</small>` : ''
  }${agent.model ? `<small>${escape(agent.model)}</small>` : ''}</p>`;
}

/** 'Agora': a atividade atual com ícone; quando ocioso, o fim do último turno. */
function agoraHtml(agent: Agent, ultima: Activity | undefined): string {
  if (agent.status === 'idle') {
    if (agent.lastTurnMs === undefined) return `<p class="vazio">${t('det.sem_turno')}</p>`;
    const quando = agent.lastTurnAt ? ` · <span class="det-ha" data-ha="${agent.lastTurnAt}">${tempoRelativo(agent.lastTurnAt)}</span>` : '';
    return `<p class="det-ativ">${t('det.concluiu', { duracao: duracaoTurno(agent.lastTurnMs) })}${quando}</p>`;
  }
  const icone = agent.status === 'waiting' ? '✋' : ultima ? iconeDoKind(ultima.kind, ultima.text, ultima.detail) : '•';
  const texto = tActivity(agent.activity || ultima?.text || '') || '—';
  const cron = cronometro(agent);
  return `<p class="det-ativ"><span class="det-ico" aria-hidden="true">${icone}</span> ${escape(texto)}${cron ? ` ${cron}` : ''}</p>`;
}

/** Um item da linha do tempo: ícone, texto curto, 'há X'. O comando (cru) vai num detalhe recolhível. */
function linhaItem(a: Activity): string {
  const chave = `cmd:${a.id}`;
  const cmd = a.detail ? `<details class="tempo-cmd" data-chave="${escape(chave)}"><summary>${t('det.comando')}</summary><code>${escape(a.detail.slice(0, 140))}</code></details>` : '';
  return `<li class="kind-${escape(a.kind)}"><span class="tempo-ico" aria-hidden="true">${iconeDoKind(a.kind, a.text, a.detail)}</span><span class="tempo-txt">${escape(githubFeedTexto(a) ?? tActivity(a.text))}</span> <time datetime="${new Date(a.at).toISOString()}" title="${hora(a.at, true)}" data-ha="${a.at}">${tempoRelativo(a.at)}</time>${cmd}</li>`;
}

/** Vida social do personagem (por id do elenco = Agent.look). Sem dado ainda, mostra o vazio. */
function socialHtml(agent: Agent, snapshot: OfficeSnapshot): string {
  const s = snapshot.social?.[agent.look];
  if (!s) return `<section class="social"><h4>${t('social.titulo')}</h4><p class="vazio">${t('social.vazio')}</p></section>`;
  const amigos = s.friends ?? [];
  const rivais = s.rivals ?? [];
  const hobbies = s.hobbies ?? [];
  const extrato = s.ledger ?? [];
  // a roda de agora (TV, jogo, papo…) vem do campus e se atualiza sozinha (montaPainelRodas); sem ela, a atividade do servidor
  const base = s.activity && s.activity.until > Date.now() ? `${s.activity.emoji} ${s.activity.text}` : t('social.livre');
  const fazendo = `<p class="social-faz" data-roda-agente="${escape(agent.id)}" data-roda-base="${escape(base)}">${escape(rodaAtual(agent.id) ?? base)}</p><p class="social-traco">${t('roda.traco')}: <b>${t(`traco.${tracoDoAgente(agent.id)}` as Chave)}</b></p>`;
  // chips; sem itens, o texto do vazio (ou nada, quando o vazio é '')
  const lista = (itens: string[], vazio: string) => (itens.length ? `<ul class="chips">${itens.map((i) => `<li>${i}</li>`).join('')}</ul>` : vazio ? `<p class="vazio">${vazio}</p>` : '');
  return `<section class="social">
      <h4>${t('social.titulo')}</h4>
      <div class="social-topo"><p class="social-saldo">🪙 <b>${s.coins ?? 0}</b> ${t('social.moedas')}</p><p>${t('social.ganhos', { n: s.earnedWorking ?? 0 })}</p></div>
      <p>${plural('social.vitoria', s.wins ?? 0)} · ${plural('social.derrota', s.losses ?? 0)}</p>
      <h5>${t('social.fazendo')}</h5>
      ${fazendo}
      <p class="social-bordao">${t('social.bordao', { texto: escape(s.catchphrase ?? '') })}</p>
      <h5>${t('social.hobbies')}</h5>${lista(hobbies.map((h) => `${escape(h.emoji)} ${escape(h.label)}`), '')}
      <h5>${t('social.amizades')}</h5>${lista(amigos.map((f) => escape(f.name)), t('social.sem_amigos'))}
      <h5>${t('social.rivalidades')}</h5>${lista(rivais.map((r) => `${escape(r.name)} (${r.score?.[0] ?? 0}×${r.score?.[1] ?? 0})`), t('social.sem_rivais'))}
      <details class="social-extrato" data-chave="extrato:${escape(agent.id)}"><summary>▸ ${t('social.extrato')}</summary>${
        extrato.length
          ? `<ol>${extrato
              .map((l) => `<li class="${l.delta < 0 ? 'neg' : 'pos'}"><b>${l.delta > 0 ? '+' : ''}${l.delta}</b> ${escape(l.reason)} <time data-ha="${l.at}">${tempoRelativo(l.at)}</time></li>`)
              .join('')}</ol>`
          : `<p class="vazio">${t('social.extrato_vazio')}</p>`
      }</details>
    </section>`;
}

// ------------------------------------------------------------------ conversa (B22)

interface ConversaItem {
  at: number;
  role: 'voce' | 'agente' | 'ferramenta';
  text: string;
}

/** Conversa aberta no painel do agente: só leitura, atualizada a cada CONVERSA_MS. */
interface ConversaAberta {
  agentId: string;
  itens?: ConversaItem[];
  erro?: string;
  timer?: number;
}

const CONVERSA_MS = 5000;
const PAPEL: Record<ConversaItem['role'], 'conversa.voce' | 'conversa.agente' | 'conversa.ferramenta'> = {
  voce: 'conversa.voce',
  agente: 'conversa.agente',
  ferramenta: 'conversa.ferramenta',
};
let conversa: ConversaAberta | null = null;

/** Botão 'Ver conversa' do painel do agente. */
function bindConversa(agent: Agent): void {
  document.querySelector<HTMLButtonElement>('[data-conversa]')?.addEventListener('click', () => abreConversa(agent.id));
}

/** Abre a conversa no painel e começa a atualizar a cada 5 s. */
function abreConversa(agentId: string): void {
  fechaConversa();
  const aberta: ConversaAberta = { agentId };
  aberta.timer = window.setInterval(() => void atualizaConversa(), CONVERSA_MS);
  conversa = aberta;
  void atualizaConversa();
  renderDetail();
}

/** Fecha a conversa e para a atualização. */
function fechaConversa(): void {
  if (conversa?.timer !== undefined) window.clearInterval(conversa.timer);
  conversa = null;
}

async function atualizaConversa(): Promise<void> {
  const aberta = conversa;
  if (!aberta) return;
  const response = await fetch(`/api/agents/${encodeURIComponent(aberta.agentId)}/transcript?limit=80`).catch(() => null);
  const json = (await response?.json().catch(() => undefined)) as { ok?: boolean; items?: ConversaItem[]; error?: string } | undefined;
  // fechou ou trocou de agente enquanto a resposta vinha
  if (conversa !== aberta) return;
  if (json?.ok && json.items) {
    aberta.itens = json.items;
    aberta.erro = undefined;
  } else {
    aberta.erro = json?.error ?? t('erro.nao_deu');
  }
  renderDetail();
}

/**
 * Painel da conversa. A lista só é refeita quando o conteúdo muda; quem estava no fim
 * continua no fim, quem subiu para ler continua no mesmo ponto.
 */
function pintaConversa(agent: Agent): void {
  const antiga = detalhe.querySelector<HTMLElement>('.conversa-lista');
  const noFim = !antiga || antiga.scrollHeight - antiga.scrollTop - antiga.clientHeight < 24;
  const topo = antiga?.scrollTop ?? 0;
  if (!trocaHtml(detalhe, conversaHtml(agent))) return;
  detalhe.querySelector<HTMLButtonElement>('[data-voltar]')?.addEventListener('click', () => {
    fechaConversa();
    renderDetail();
  });
  const nova = detalhe.querySelector<HTMLElement>('.conversa-lista');
  if (nova) nova.scrollTop = noFim ? nova.scrollHeight : topo;
}

function conversaHtml(agent: Agent): string {
  const aberta = conversa;
  const itens = aberta?.itens;
  const erro = aberta?.erro ? `<p class="erro" role="alert">${escape(aberta.erro)}</p>` : '';
  let corpo = '';
  if (itens === undefined) {
    if (!erro) corpo = `<p class="vazio">${t('conversa.carregando')}</p>`;
  } else {
    corpo = `<ol class="conversa-lista" tabindex="0">${
      itens.length ? itens.map(conversaItem).join('') : `<li class="vazio">${t('conversa.vazia')}</li>`
    }</ol>`;
  }
  return `<header class="det-cab"><button class="conversa-voltar" data-voltar>← ${t('conversa.voltar')}</button><span class="conversa-titulo">${t('conversa.titulo')} · ${escape(agent.name)}</span><button class="fechar" data-close aria-label="${t('acao.fechar')}">✕</button></header>
    <p class="conversa-aviso">${t('conversa.aviso')}</p>
    ${erro}${corpo}`;
}

function conversaItem(item: ConversaItem): string {
  return `<li class="conversa-item conversa-${item.role}"><div class="conversa-cab"><b>${t(PAPEL[item.role])}</b><time>${hora(item.at, true)}</time></div><p class="conversa-texto">${escape(item.text)}</p></li>`;
}

/** O que a pessoa marcou num pedido de pergunta. Fica aqui (não no HTML) para sobreviver ao repintar do painel. */
interface RascunhoPergunta {
  /** Posições das opções escolhidas, por pergunta. */
  escolhas: Record<number, number[]>;
  /** Texto livre ('Outro'), por pergunta. */
  outro: Record<number, string>;
  recusando: boolean;
  motivo: string;
}

const rascunhos = new Map<string, RascunhoPergunta>();
/** Cartões de pergunta fechados com "Responder no terminal": somem daqui, o pedido segue aberto. */
const fechadosNoTerminal = new Set<string>();
/** Pedidos que o modal do celular deixou para trás (o membro fechou o aviso). Só o modal olha esta lista. */
const dispensados = new Set<string>();
/** Pedidos que já vibraram no celular: um toque por pedido, mesmo se o evento chegar de novo. */
const vibrados = new Set<string>();
/** Pedido que o modal mostra agora (para focar a caixa só quando muda). */
let missaoAtual = '';
/** O modal de pedido do celular (montado no start; fica escondido no desktop). */
let missao: HTMLElement | undefined;

function rascunhoDe(permId: string): RascunhoPergunta {
  let r = rascunhos.get(permId);
  if (!r) {
    r = { escolhas: {}, outro: {}, recusando: false, motivo: '' };
    rascunhos.set(permId, r);
  }
  return r;
}

function permissionCard(p: PermissionRequest): string {
  if (p.questions) return perguntaCard(p, p.questions);
  // "não perguntar de novo" (sugestão do Claude Code) e interromper ao recusar: só quando o pedido traz
  const sugestoes = (p.suggestions ?? []).map((s) => `<button class="sempre" data-decision="allow" data-sugestao="${s.index}">${t('perm.sempre')}<small>${escape(s.label)}</small></button>`).join('');
  const interromper = p.provider === 'claude' ? `<label class="interromper" title="${escape(t('perm.interromper_dica'))}"><input type="checkbox" data-interromper /> ${t('perm.interromper')}</label>` : '';
  return `<div class="pedido" data-perm="${escape(p.id)}">
    <strong>${escape(p.title)}</strong>
    <p>${escape(p.text)}</p>
    ${p.input ? `<code>${escape(p.input.slice(0, 240))}</code>` : ''}
    ${sugestoes ? `<div class="sugestoes">${sugestoes}</div>` : ''}
    <div class="acoes"><button class="aprovar" data-decision="allow">${t('det.aprovar')}</button><button class="recusar" data-decision="deny">${t('det.recusar')}</button></div>
    ${interromper}
  </div>`;
}

/**
 * Cartão de uma pergunta do AskUserQuestion: cada pergunta com as opções (rádio, ou caixa se
 * for múltipla) e o 'Outro'. Quem confere as escolhas é o servidor; aqui só se monta o pedido.
 */
function perguntaCard(p: PermissionRequest, perguntas: PermissionQuestion[]): string {
  if (fechadosNoTerminal.has(p.id)) return '';
  const r = rascunhoDe(p.id);
  const base = `perg-${p.id.replace(/[^\w-]/g, '_')}`;
  const blocos = perguntas
    .map((q) => {
      const marcadas = r.escolhas[q.index] ?? [];
      const tipo = q.multiSelect ? 'checkbox' : 'radio';
      const opcoes = q.options
        .map((o) => {
          const id = `${base}-q${q.index}-o${o.index}`;
          return `<label class="opcao"><input id="${id}" type="${tipo}" name="${base}-q${q.index}" value="${o.index}" data-pergunta="${q.index}"${marcadas.includes(o.index) ? ' checked' : ''} /><span><b>${escape(o.label)}</b>${o.description ? `<small>${escape(o.description)}</small>` : ''}</span></label>`;
        })
        .join('');
      return `<fieldset class="pergunta-bloco">
        <legend>${q.header ? `<span class="selo">${escape(q.header)}</span> ` : ''}${escape(q.question)}</legend>
        <small class="pergunta-dica">${t(q.multiSelect ? 'perg.multipla' : 'perg.unica')}</small>
        ${opcoes}
        <label class="opcao outro"><span><b>${t('perg.outro')}</b></span><input id="${base}-q${q.index}-outro" type="text" maxlength="2000" data-outro="${q.index}" value="${escape(r.outro[q.index] ?? '')}" placeholder="${escape(t('perg.outro_ph'))}" autocomplete="off" /></label>
      </fieldset>`;
    })
    .join('');
  const motivo = r.recusando
    ? `<div class="pergunta-motivo"><input id="${base}-motivo" type="text" maxlength="500" data-motivo value="${escape(r.motivo)}" placeholder="${escape(t('perg.motivo'))}" aria-label="${escape(t('perg.motivo'))}" autocomplete="off" /><button class="recusar" id="${base}-recusar-enviar" data-pergunta-acao="recusar-enviar">${t('perg.recusar_enviar')}</button></div>`
    : '';
  return `<div class="pedido pedido-perguntas" data-perm="${escape(p.id)}">
    <strong>${t('perg.titulo')}</strong>
    <div class="pedido-resposta">
      ${blocos}
      <p class="pergunta-erro" role="alert" data-erro hidden></p>
      <div class="acoes">
        <button class="aprovar" id="${base}-responder" data-pergunta-acao="responder">${t('perg.responder')}</button>
        <button class="recusar" id="${base}-recusar" data-pergunta-acao="recusar-abrir" aria-expanded="${r.recusando}">${t('perg.recusar')}</button>
        <button id="${base}-terminal" data-pergunta-acao="terminal" title="${escape(t('perg.terminal_dica'))}">${t('perg.terminal')}</button>
      </div>
      ${motivo}
    </div>
  </div>`;
}

/** Botões do cartão de pergunta: responder (manda as escolhas), recusar (com motivo opcional) ou fechar sem decidir. */
async function acaoDaPergunta(botao: HTMLElement): Promise<void> {
  const cartao = botao.closest<HTMLElement>('[data-perm]');
  const perm = cartao?.dataset.perm;
  if (!cartao || !perm) return;
  const acao = botao.dataset.perguntaAcao;
  const r = rascunhoDe(perm);
  if (acao === 'terminal') {
    fechadosNoTerminal.add(perm);
    rascunhos.delete(perm);
    repintaPedidos();
    return;
  }
  if (acao === 'recusar-abrir') {
    r.recusando = !r.recusando;
    repintaPedidos();
    if (r.recusando) cartao.querySelector<HTMLInputElement>('[data-motivo]')?.focus();
    return;
  }
  // a ordem das perguntas é a do próprio cartão; o servidor confere cada escolha
  const perguntas = [...cartao.querySelectorAll<HTMLInputElement>('[data-outro]')].map((el) => Number(el.dataset.outro));
  const body =
    acao === 'responder'
      ? {
          decision: 'answer',
          answers: perguntas.map((q) => {
            const opcoes = r.escolhas[q] ?? [];
            const outro = (r.outro[q] ?? '').trim();
            return { question: q, ...(opcoes.length ? { options: opcoes } : {}), ...(outro ? { other: outro } : {}) };
          }),
        }
      : { decision: 'deny', ...(r.motivo.trim() ? { text: r.motivo.trim() } : {}) };
  const erro = cartao.querySelector<HTMLElement>('[data-erro]');
  botao.setAttribute('disabled', '');
  try {
    const res = await fetch(`/api/permissions/${encodeURIComponent(perm)}/decision`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      rascunhos.delete(perm);
      return;
    }
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    if (erro) {
      erro.textContent = json.error ?? t('erro.nao_deu');
      erro.hidden = false;
    }
  } catch {
    if (erro) {
      erro.textContent = t('erro.nao_deu');
      erro.hidden = false;
    }
  }
  botao.removeAttribute('disabled');
}

// ------------------------------------------------------------------ modal de missão (celular, B29-M4)

/** O modal do celular: um elemento só, escondido até haver pedido; os botões e os rascunhos são os do cartão. */
function montaMissao(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'missao';
  el.hidden = true;
  el.addEventListener('click', (evento) => void aoClicarMissao(evento));
  el.addEventListener('change', aoMudarPergunta);
  el.addEventListener('input', aoDigitarPergunta);
  document.body.append(el);
  return el;
}

/** Redesenha o cartão do painel e o modal: o mesmo pedido pode estar nos dois. */
function repintaPedidos(): void {
  renderDetail();
  renderMissao();
}

/** Mostra o primeiro pedido da fila no modal (só no celular). Sem pedido na fila, o modal some. */
function renderMissao(): void {
  const el = missao;
  if (!el) return;
  const snapshot = state.snapshot;
  const fila = snapshot && ehCelular() ? filaDePedidos(snapshot.permissions, new Set([...fechadosNoTerminal, ...dispensados])) : undefined;
  const pedido = fila?.atual;
  if (!snapshot || !fila || !pedido) {
    el.hidden = true;
    missaoAtual = '';
    return;
  }
  const agente = snapshot.agents.find((a) => a.id === pedido.agentId);
  const novo = el.hidden || missaoAtual !== pedido.id;
  missaoAtual = pedido.id;
  el.hidden = false;
  trocaHtml(el, missaoHtml(pedido, agente, fila));
  if (novo) el.querySelector<HTMLElement>('.missao-caixa')?.focus({ preventScroll: true });
}

/** O cartão do modal: o que o agente quer, em letra grande, e os botões (ou só o aviso, para o membro). */
function missaoHtml(pedido: PermissionRequest, agente: Agent | undefined, fila: { posicao: number; total: number }): string {
  const admin = state.role === 'admin';
  const r = rascunhoDe(pedido.id);
  const perguntas = pedido.questions;
  const quem = agente ? `<img class="missao-avatar" src="${avatarUrl(lookOf(agente))}" alt="" width="56" height="56" />` : '';
  const cli = PROVIDER_LABELS[pedido.provider] ?? pedido.provider;
  const sobre = [agente?.project, cli].filter(Boolean).join(' · ');
  let corpo = '';
  if (perguntas) corpo = admin ? missaoPerguntas(perguntas, r) : perguntas.map((q) => `<p class="missao-texto">${escape(q.question)}</p>`).join('');
  else if (pedido.input) corpo = `<pre class="missao-bloco">${escape(pedido.input.slice(0, 600))}</pre>`;
  let acoes: string;
  if (!admin) acoes = `<p class="missao-so-aviso">${t('missao.so_admin')}</p><button type="button" class="missao-entendi" data-missao="fechar">${t('missao.entendi')}</button>`;
  else if (perguntas) acoes = missaoAcoesPergunta(r);
  else {
    const sugestoes = (pedido.suggestions ?? []).map((s) => `<button type="button" class="missao-sempre" data-missao="allow" data-sugestao="${s.index}">${t('perm.sempre')}<small>${escape(s.label)}</small></button>`).join('');
    const interromper = pedido.provider === 'claude' ? `<label class="missao-interromper"><input type="checkbox" data-interromper /> ${t('perm.interromper')}</label>` : '';
    acoes = `<div class="missao-acoes">
      ${sugestoes}
      <button type="button" class="missao-aprovar" data-missao="allow">${t('det.aprovar')}</button>
      <button type="button" class="missao-recusar" data-missao="deny">${t('det.recusar')}</button>
      ${interromper}
      <button type="button" class="missao-terminal" data-missao="terminal">${t('perg.terminal')}</button>
    </div>`;
  }
  return `<div class="missao-caixa" role="alertdialog" aria-modal="true" aria-labelledby="missao-titulo" data-perm="${escape(pedido.id)}" tabindex="-1">
    <header class="missao-cab"><span class="missao-selo">${t('missao.titulo')}</span>${fila.total > 1 ? `<span class="missao-contagem">${t('missao.de', { n: fila.posicao, total: fila.total })}</span>` : ''}</header>
    <div class="missao-quem">${quem}<div><b>${escape(agente?.name ?? pedido.agentId)}</b><small>${escape(sobre)}</small></div></div>
    <h2 id="missao-titulo" class="missao-titulo">${escape(perguntas ? t('perg.titulo') : pedido.title)}</h2>
    ${perguntas ? '' : `<p class="missao-texto">${escape(pedido.text)}</p>`}
    ${corpo}
    <p class="missao-erro" role="alert" data-erro hidden></p>
    ${acoes}
  </div>`;
}

/** As perguntas do AskUserQuestion no modal: uma opção grande por linha (rádio ou caixa) e o 'Outro'. */
function missaoPerguntas(perguntas: PermissionQuestion[], r: RascunhoPergunta): string {
  return perguntas
    .map((q) => {
      const marcadas = r.escolhas[q.index] ?? [];
      const tipo = q.multiSelect ? 'checkbox' : 'radio';
      const opcoes = q.options
        .map(
          (o) =>
            `<label class="missao-opcao"><input type="${tipo}" name="missao-q${q.index}" value="${o.index}" data-pergunta="${q.index}"${marcadas.includes(o.index) ? ' checked' : ''} /><span><b>${escape(o.label)}</b>${o.description ? `<small>${escape(o.description)}</small>` : ''}</span></label>`,
        )
        .join('');
      return `<fieldset class="pergunta-bloco missao-pergunta">
        <legend>${q.header ? `<span class="selo">${escape(q.header)}</span> ` : ''}${escape(q.question)}</legend>
        <small class="pergunta-dica">${t(q.multiSelect ? 'perg.multipla' : 'perg.unica')}</small>
        ${opcoes}
        <label class="missao-opcao missao-outro"><span><b>${t('perg.outro')}</b></span><input id="missao-outro-${q.index}" type="text" maxlength="2000" data-outro="${q.index}" value="${escape(r.outro[q.index] ?? '')}" placeholder="${escape(t('perg.outro_ph'))}" autocomplete="off" /></label>
      </fieldset>`;
    })
    .join('');
}

/** Os botões de uma pergunta no modal: Responder, Recusar (com motivo) e Responder no terminal. */
function missaoAcoesPergunta(r: RascunhoPergunta): string {
  const motivo = r.recusando
    ? `<div class="missao-motivo"><input id="missao-motivo" type="text" maxlength="500" data-motivo value="${escape(r.motivo)}" placeholder="${escape(t('perg.motivo'))}" aria-label="${escape(t('perg.motivo'))}" autocomplete="off" /><button type="button" class="missao-recusar" data-pergunta-acao="recusar-enviar">${t('perg.recusar_enviar')}</button></div>`
    : '';
  return `<div class="missao-acoes">
      <button type="button" class="missao-aprovar" data-pergunta-acao="responder">${t('perg.responder')}</button>
      <button type="button" class="missao-recusar" data-pergunta-acao="recusar-abrir" aria-expanded="${r.recusando}">${t('perg.recusar')}</button>
      <button type="button" class="missao-terminal" data-missao="terminal">${t('perg.terminal')}</button>
    </div>
    ${motivo}`;
}

/** Os botões do modal: aprovar ou recusar, responder no terminal, fechar o aviso (membro), ou os da pergunta. */
async function aoClicarMissao(evento: Event): Promise<void> {
  const alvo = evento.target as HTMLElement;
  const perguntaAcao = alvo.closest<HTMLElement>('[data-pergunta-acao]');
  if (perguntaAcao) return acaoDaPergunta(perguntaAcao);
  const botao = alvo.closest<HTMLElement>('[data-missao]');
  const id = missao?.querySelector<HTMLElement>('.missao-caixa')?.dataset.perm;
  if (!botao || !id) return;
  const acao = botao.dataset.missao;
  if (acao === 'allow' || acao === 'deny') return decideMissao(id, acao, botao);
  if (acao === 'terminal') {
    // o modal sai da frente e a conversa abre; o pedido segue até alguém decidir (e some quando decidir)
    const agenteId = state.snapshot?.permissions.find((p) => p.id === id)?.agentId;
    fechadosNoTerminal.add(id);
    rascunhos.delete(id);
    repintaPedidos();
    if (agenteId) vivo.open(agenteId);
  } else if (acao === 'fechar') {
    dispensados.add(id);
    repintaPedidos();
  }
}

/** Aprovar ou recusar pelo modal. Dando certo, o pedido sai da fila quando o evento de resolvido chega. */
async function decideMissao(id: string, decisao: 'allow' | 'deny', botao: HTMLElement): Promise<void> {
  const erro = missao?.querySelector<HTMLElement>('[data-erro]');
  botao.setAttribute('disabled', '');
  const interromper = missao?.querySelector<HTMLInputElement>('[data-interromper]')?.checked === true;
  const res = await fetch(`/api/permissions/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpoDaDecisao(decisao, { sugestao: sugestaoDoBotao(botao.dataset.sugestao), interromper })),
  }).catch(() => null);
  if (res?.ok) return;
  const json = (await res?.json().catch(() => ({}))) as { error?: string } | undefined;
  if (erro) {
    erro.textContent = json?.error ?? t('erro.nao_deu');
    erro.hidden = false;
  }
  botao.removeAttribute('disabled');
}

/** Celular: o terminal ao vivo do agente selecionado; sem seleção, o que mais precisa de você (ou o primeiro). */
function abreVivoDoCelular(): void {
  const agentes = state.snapshot?.agents ?? [];
  const alvo =
    agentes.find((a) => a.id === state.selectedAgent) ??
    agentes.find((a) => a.status === 'waiting') ??
    agentes.find((a) => a.status === 'working' || a.status === 'thinking') ??
    agentes[0];
  if (alvo) vivo.open(alvo.id);
}

/** Data local AAAA-MM-DD (a mesma conta de dayKey no servidor). */
function chaveDoDia(d: Date): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

// ------------------------------------------------------------------ terminal (B23/B25)

/** Sessão agzos-* como chega de GET /api/terminal. */
interface TerminalSessao {
  id: string;
  name: string;
  cli: string;
  cwd: string;
  created: number;
}

/** Intervalo da tela do terminal: a tela e a lista são lidas a cada segundo enquanto o painel está aberto. */
const TERMINAL_MS = 1000;
/** Sessão escolhida na lista (undefined = nenhuma). */
let terminalSel: string | undefined;
/** Timer da atualização; undefined quando o painel não está aberto. */
let terminalTimer: number | undefined;
/** Uma leitura por vez: se a anterior ainda não voltou, o tique seguinte pula. */
let terminalLendo = false;
/** Pedido do painel de um agente (T / Abrir terminal): na próxima leitura, escolhe a sessão dele. */
let terminalDono: { cwd: string; cli: string } | undefined;
/** O agente pedido não tem sessão no terminal do escritório: a tela mostra o aviso, sem cair em outra sessão. */
let terminalSemSessao = false;

/** Terminal ao vivo do agente (botão do painel ou T): abre o painel na sessão dele (mesma pasta e CLI). */
function abreTerminalDoAgente(agentId: string): void {
  const agent = state.snapshot?.agents.find((a) => a.id === agentId);
  if (!agent) return;
  terminalDono = { cwd: agent.cwd, cli: agent.provider };
  terminalSel = undefined;
  abreTerminal();
}

/** Casca do painel: lista e sessão escolhida primeiro; Novo agente recolhido no fim. Estática; o timer preenche. */
function terminalCasca(): string {
  return `<header class="det-cab"><span>${t('terminal.titulo')}</span><button class="fechar" data-close aria-label="${t('acao.fechar')}">✕</button></header>
    <p class="vazio">${t('terminal.explica')}</p>
    <button class="pty-abrir" data-pty-abrir>${t('pty.titulo')}</button>
    <section><h4>${t('terminal.sessoes')}</h4><ul id="terminal-lista" class="terminal-lista" aria-label="${t('terminal.lista_aria')}"></ul></section>
    <section id="terminal-sessao" hidden>
      <div class="terminal-cab"><b id="terminal-nome"></b><button class="terminal-encerrar" data-terminal-encerrar>${t('terminal.encerrar')}</button></div>
      <pre id="terminal-tela" class="terminal-tela" tabindex="0" aria-label="${t('terminal.tela_aria')}"></pre>
      <form id="terminal-envio" class="terminal-envio">
        <input id="terminal-texto" autocomplete="off" aria-label="${t('terminal.digite')}" placeholder="${t('terminal.digite')}" />
        <button type="submit">${t('det.enviar')}</button>
        <button type="button" data-terminal-tecla="enter">${t('terminal.enter')}</button>
        <button type="button" data-terminal-tecla="ctrl-c">${t('terminal.ctrl_c')}</button>
      </form>
    </section>
    <p id="terminal-erro" class="erro" role="alert"></p>
    <details class="terminal-novo">
      <summary>${t('terminal.novo')}</summary>
      <form id="terminal-novo" class="terminal-form">
        <label>${t('terminal.cli')} <select id="terminal-cli"><option value="claude">Claude Code</option><option value="codex">Codex</option></select></label>
        <label>${t('terminal.pasta')} <input id="terminal-pasta" required autocomplete="off" placeholder="/var/www/meu-projeto" aria-describedby="terminal-pasta-dica" /></label>
        <p id="terminal-pasta-dica" class="terminal-dica">${t('terminal.pasta_dica')}</p>
        <label>${t('terminal.pedido')} <input id="terminal-pedido" autocomplete="off" /></label>
        <button type="submit">${t('terminal.criar')}</button>
      </form>
    </details>`;
}

/** Elemento dentro do painel do terminal (o painel é o conteúdo de #detalhe). */
function termEl<T extends HTMLElement = HTMLElement>(sel: string): T | null {
  return detalhe.querySelector<T>(sel);
}

/** Abre o painel do terminal e começa a atualizar a cada segundo. */
function abreTerminal(): void {
  state.detail = 'terminal';
  renderTop();
  renderDetail();
  if (terminalTimer === undefined) terminalTimer = window.setInterval(() => void atualizaTerminal(), TERMINAL_MS);
  void atualizaTerminal();
}

function paraTerminal(): void {
  if (terminalTimer !== undefined) window.clearInterval(terminalTimer);
  terminalTimer = undefined;
}

/** Uma leitura: a lista de sessões e, se há uma escolhida, a tela dela. */
async function atualizaTerminal(): Promise<void> {
  if (terminalLendo || state.detail !== 'terminal') return;
  terminalLendo = true;
  try {
    const lista = await fetch('/api/terminal')
      .then((r) => r.json() as Promise<{ ok?: boolean; sessions?: TerminalSessao[] }>)
      .catch(() => undefined);
    if (state.detail !== 'terminal') return;
    if (!lista?.ok || !lista.sessions) {
      avisaTerminal(t('erro.nao_deu'));
      return;
    }
    const sessoes = lista.sessions;
    // pedido do painel de um agente: a sessão dele (mesma pasta e CLI); sem ela, nenhuma fica escolhida
    if (terminalDono) {
      const dono = terminalDono;
      terminalDono = undefined;
      terminalSel = sessoes.find((s) => s.cwd === dono.cwd && s.cli === dono.cli)?.id;
      terminalSemSessao = terminalSel === undefined;
    }
    // a sessão escolhida sumiu (encerrada ou o processo acabou): cai na primeira da lista
    if (!terminalSemSessao && !sessoes.some((s) => s.id === terminalSel)) terminalSel = sessoes[0]?.id;
    pintaListaTerminal(sessoes);
    await pintaTelaTerminal(sessoes);
  } finally {
    terminalLendo = false;
  }
}

function pintaListaTerminal(sessoes: TerminalSessao[]): void {
  const lista = termEl('#terminal-lista');
  if (!lista) return;
  trocaHtml(
    lista,
    sessoes.length
      ? sessoes
          .map((s) => {
            const atual = s.id === terminalSel ? ' aria-current="true"' : '';
            return `<li><button id="terminal-s-${escape(s.id)}" data-terminal-abrir="${escape(s.id)}"${atual}><b>${escape(s.name)}</b><small>${escape(s.cli)} · ${escape(s.cwd ? shortPath(s.cwd) : '—')}</small></button></li>`;
          })
          .join('')
      : `<li class="vazio">${t('terminal.vazio')}</li>`,
  );
}

/** A tela da sessão escolhida. Só mexe no texto se mudou; quem estava no fim continua no fim. */
async function pintaTelaTerminal(sessoes: TerminalSessao[]): Promise<void> {
  const sessao = sessoes.find((s) => s.id === terminalSel);
  const caixa = termEl('#terminal-sessao');
  if (caixa) caixa.hidden = !sessao;
  if (!sessao) {
    avisaTerminal(terminalSemSessao ? t('det.terminal_sem_sessao') : '');
    return;
  }
  const nome = termEl('#terminal-nome');
  if (nome) nome.textContent = sessao.name;
  const leitura = await fetch(`/api/terminal/${encodeURIComponent(sessao.id)}/screen`)
    .then((r) => r.json() as Promise<{ ok?: boolean; text?: string; error?: string }>)
    .catch(() => undefined);
  // trocou de sessão ou fechou o painel enquanto a resposta vinha
  if (state.detail !== 'terminal' || terminalSel !== sessao.id) return;
  const tela = termEl<HTMLPreElement>('#terminal-tela');
  if (!tela) return;
  if (!leitura?.ok) {
    avisaTerminal(leitura?.error ?? t('erro.nao_deu'));
    return;
  }
  const texto = leitura.text?.trim() ? (leitura.text ?? '') : t('terminal.tela_vazia');
  if (tela.textContent === texto) return;
  const noFim = tela.scrollHeight - tela.scrollTop - tela.clientHeight < 24;
  tela.textContent = texto;
  if (noFim) tela.scrollTop = tela.scrollHeight;
}

function avisaTerminal(texto: string): void {
  const erro = termEl('#terminal-erro');
  if (erro) erro.textContent = texto;
}

/** POST de uma ação do terminal. Nunca lança: o erro volta como texto para mostrar. */
async function terminalPost(url: string, corpo: unknown): Promise<{ ok: boolean; id?: string; error?: string }> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(corpo),
  }).catch(() => null);
  const json = (await response?.json().catch(() => undefined)) as { ok?: boolean; id?: string; error?: string } | undefined;
  if (json?.ok) return { ok: true, id: json.id };
  return { ok: false, error: json?.error ?? t('erro.nao_deu') };
}

/** Novo agente: a CLI na pasta, com o primeiro pedido se houver. */
async function criaTerminal(): Promise<void> {
  const cli = termEl<HTMLSelectElement>('#terminal-cli')?.value ?? 'claude';
  const cwd = termEl<HTMLInputElement>('#terminal-pasta')?.value.trim() ?? '';
  const pedido = termEl<HTMLInputElement>('#terminal-pedido');
  const prompt = pedido?.value.trim() ?? '';
  const r = await terminalPost('/api/terminal/new', { cli, cwd, prompt: prompt || undefined });
  if (!r.ok) {
    avisaTerminal(t('terminal.erro', { erro: r.error ?? '' }));
    return;
  }
  terminalSel = r.id;
  terminalSemSessao = false;
  if (pedido) pedido.value = '';
  avisaTerminal(t('terminal.aberto'));
  void atualizaTerminal();
}

/** Digita e/ou aperta Enter, ou manda Ctrl-C, na sessão escolhida. */
async function enviaTerminal(corpo: { text?: string; enter?: boolean; interrupt?: boolean }): Promise<void> {
  const id = terminalSel;
  if (!id) {
    avisaTerminal(t('terminal.escolha'));
    return;
  }
  const r = await terminalPost(`/api/terminal/${encodeURIComponent(id)}/keys`, corpo);
  if (!r.ok) {
    avisaTerminal(t('terminal.erro', { erro: r.error ?? '' }));
    return;
  }
  avisaTerminal('');
  // limpa o campo só se ainda é o texto enviado (a pessoa pode ter continuado digitando)
  const campo = termEl<HTMLInputElement>('#terminal-texto');
  if (campo && corpo.text && campo.value === corpo.text) campo.value = '';
  void atualizaTerminal();
}

/** Encerrar: pede confirmação e mata só a sessão agzos-<id> escolhida. */
async function encerraTerminal(): Promise<void> {
  const id = terminalSel;
  if (!id || !window.confirm(t('terminal.encerrar_confirma'))) return;
  const r = await terminalPost(`/api/terminal/${encodeURIComponent(id)}/kill`, {});
  if (!r.ok) {
    avisaTerminal(t('terminal.erro', { erro: r.error ?? '' }));
    return;
  }
  terminalSel = undefined;
  avisaTerminal(t('terminal.encerrada'));
  void atualizaTerminal();
}

/** Um ouvinte só para o painel do terminal (a casca é trocada, o #detalhe não). */
function ligaTerminal(): void {
  detalhe.addEventListener('click', (event) => {
    const alvo = event.target as HTMLElement;
    const abrir = alvo.closest<HTMLElement>('[data-terminal-abrir]')?.dataset.terminalAbrir;
    if (abrir) {
      terminalSel = abrir;
      terminalSemSessao = false;
      void atualizaTerminal();
      return;
    }
    if (alvo.closest('[data-terminal-encerrar]')) {
      void encerraTerminal();
      return;
    }
    const tecla = alvo.closest<HTMLElement>('[data-terminal-tecla]')?.dataset.terminalTecla;
    if (tecla === 'enter') void enviaTerminal({ enter: true });
    if (tecla === 'ctrl-c') void enviaTerminal({ interrupt: true });
  });
  detalhe.addEventListener('submit', (event) => {
    const form = event.target as HTMLElement;
    if (form.id !== 'terminal-novo' && form.id !== 'terminal-envio') return;
    event.preventDefault();
    if (form.id === 'terminal-novo') {
      void criaTerminal();
      return;
    }
    // Enviar: o texto (se houver) seguido de Enter
    const texto = termEl<HTMLInputElement>('#terminal-texto')?.value ?? '';
    void enviaTerminal({ text: texto, enter: true });
  });
}
ligaTerminal();

/** Assumir (B25): só agente do Claude Code; abre 'claude --resume' na pasta dele no terminal. */
function assumirHtml(agent: Agent): string {
  if (agent.provider !== 'claude' || agent.id.startsWith('demo:')) return '';
  return `<button class="terminal-assumir" data-acao="assumir">${t('det.assumir')}</button><p class="terminal-dica">${t('det.assumir_dica')}</p>`;
}

/** Assumir: o erro, se houver, sai no recado do painel; o botão mantém o seu texto. */
async function assumeAgente(agentId: string): Promise<void> {
  // terminal interativo liberado: encerra a sessão do outro terminal (se provar que é ela) e continua aqui
  if (ptyEstado.uso.usavel) {
    const agent = state.snapshot?.agents.find((a) => a.id === agentId);
    if (agent?.provider === 'claude') {
      if (!confirm(t('pty.assumir_confirma'))) return;
      const r = await ptyPost('/api/pty/takeover', { agentId });
      if (!r.ok) {
        diz(r.error ?? t('erro.nao_deu'));
        return;
      }
      await ptyEstado.refresh();
      if (!sessaoDoAgente(ptyEstado.sessoes, agent)) ptyJanela.open(r.id);
      vivo.open(agentId);
      return;
    }
  }
  const r = await terminalPost('/api/terminal/resume', { agentId });
  if (!r.ok) {
    diz(r.error ?? t('erro.nao_deu'));
    return;
  }
  terminalSel = r.id;
  terminalSemSessao = false;
  terminalDono = undefined;
  abreTerminal();
}

/**
 * Ações do painel do agente (seguir, centralizar, sala, terminal, assumir). Um ouvinte só: o painel
 * é refeito a cada evento, então nada aqui fica ligado ao botão antigo.
 */
function ligaAcoesAgente(): void {
  detalhe.addEventListener('click', (event) => {
    const acao = (event.target as HTMLElement).closest<HTMLElement>('[data-acao]');
    const agentId = state.selectedAgent;
    if (!acao || !agentId || state.detail !== 'agent') return;
    switch (acao.dataset.acao) {
      case 'seguir':
        if (renderer?.follows(agentId)) renderer.stopFollow();
        else renderer?.select(agentId);
        break;
      case 'centralizar':
        renderer?.center(agentId);
        break;
      case 'editar-personagem': {
        const quem = state.snapshot?.agents.find((a) => a.id === agentId);
        if (quem) void editaPersonagem(quem);
        break;
      }
      case 'sala':
        renderer?.centerRoom(acao.dataset.sala ?? '');
        break;
      case 'terminal':
        vivo.open(agentId);
        break;
      case 'assumir':
        void assumeAgente(agentId);
        break;
      default:
        break;
    }
  });
}
ligaAcoesAgente();

// ------------------------------------------------------------------ terminal ao vivo (janela)

/** Sessões tmux do escritório (agzos-*), para o "Encerrar" da janela. Lidas enquanto ela está aberta. */
let sessoesEscritorio: TerminalSessao[] = [];
const ptyEstado = new PtyEstado(() => state.role === 'admin');
const vivo = new LiveTerminal(document.body, {
  agent: (id) => state.snapshot?.agents.find((a) => a.id === id),
  siblings: (agent) => (state.snapshot?.agents ?? []).filter((a) => !a.parentId && a.project === agent.project),
  tag: (agent) => ({ label: PROVIDER_TAGS[agent.provider] ?? agent.provider, color: PROVIDER_COLORS[agent.provider] ?? '#555' }),
  isAdmin: () => state.role === 'admin',
  takeover: (agent) => void assumeAgente(agent.id),
  officeTerminal: (agent) => sessoesEscritorio.find((s) => s.cwd === agent.cwd && s.cli === agent.provider)?.id,
  stopTerminal: async (id) => {
    await terminalPost(`/api/terminal/${encodeURIComponent(id)}/kill`, {});
    sessoesEscritorio = sessoesEscritorio.filter((s) => s.id !== id);
  },
  onChange: () => renderDetail(),
  refreshMs: () => (state.snapshot?.settings?.theme.liveRefreshS ?? 2) * 1000,
  pty: {
    sessao: (agent) => (ptyEstado.uso.usavel ? sessaoDoAgente(ptyEstado.sessoes, agent)?.id : undefined),
    disponivel: () => ptyEstado.uso.usavel,
    monta: (host, id) => {
      const tela = new PtyTela(host, () => void ptyEstado.refresh());
      tela.attach(id);
      // no celular o teclado não sobe sozinho ao abrir (o mesmo cuidado da caixa de mensagem da conversa)
      if (!ehCelular()) tela.focus();
      return { destroy: () => tela.destroy() };
    },
    encerra: async (agent, id) => {
      const r = id ? await ptyPost(`/api/pty/${encodeURIComponent(id)}/kill`) : await ptyPost('/api/pty/stop', { agentId: agent.id });
      await ptyEstado.refresh();
      return r.ok ? undefined : (r.error ?? t('erro.nao_deu'));
    },
  },
});
// terminal interativo (6.1): o estado vem do servidor; a janela própria serve a Nova sessão
const ptyJanela = montaPtyJanela(document.body, {
  estado: ptyEstado,
  isAdmin: () => state.role === 'admin',
  contas: () => state.snapshot?.settings?.clis ?? [],
  pastas: () => pastasSugeridas(state.snapshot?.agents ?? []),
});
ptyEstado.onChange(() => vivo.update());
ptyEstado.start();
window.setInterval(() => {
  if (!vivo.isOpen || state.role !== 'admin') return;
  void fetch('/api/terminal')
    .then((r) => r.json() as Promise<{ ok?: boolean; sessions?: TerminalSessao[] }>)
    .then((j) => {
      if (j.ok && j.sessions) sessoesEscritorio = j.sessions;
    })
    .catch(() => {});
}, 5000);

// histórico de sessões (T4): janela suspensa; abre pelo evento 'agzos:painel' (barra de ferramentas)
mountHistory(document.body, {
  agentes: () => state.snapshot?.agents ?? [],
  contas: () => state.snapshot?.settings?.clis ?? [],
  abreVivo: (agentId) => vivo.open(agentId),
  abreEncerrada: (sessao) => vivo.openEncerrada(sessao),
});

// ------------------------------------------------------------------ entregas (janela e selo da barra)

const entregas = montaEntregas();

// ------------------------------------------------------------------ interação

function selectAgent(agentId: string | null, moveCamera: boolean): void {
  state.selectedAgent = agentId;
  state.detail = agentId ? 'agent' : state.detail === 'agent' ? null : state.detail;
  if (moveCamera) renderer?.select(agentId);
  render();
}

busca.addEventListener('input', () => {
  state.search = busca.value;
  renderSidebar();
});
// contador de shells do topo: o clique vai ao próximo agente com shell rodando (5.1)
numeros.addEventListener('click', (evento) => {
  if (!(evento.target as HTMLElement).closest('[data-shells]')) return;
  const id = proximoDaFila(agentesComStatus(state.snapshot?.agents ?? [], 'shell'), state.selectedAgent);
  if (id) selectAgent(id, true);
});

/** Atalhos: '/' busca, Esc fecha, j/k andam pela lateral, f segue o agente, t abre o terminal dele e '?' mostra a ajuda. */
document.addEventListener('keydown', (e) => {
  const ativo = document.activeElement;
  const digitando = ativo instanceof HTMLInputElement || ativo instanceof HTMLTextAreaElement || ativo instanceof HTMLSelectElement;
  if (e.key === 'Escape') {
    if (digitando) (ativo as HTMLElement).blur();
    if (state.detail === 'bridge' || state.detail === 'terminal') {
      state.detail = null;
      btnPonte.setAttribute('aria-pressed', 'false');
      render();
    } else selectAgent(null, true);
    return;
  }
  // digitando, Ctrl/Alt/Meta (atalhos do navegador) ou tela de login: nada a fazer aqui
  if (digitando || e.ctrlKey || e.metaKey || e.altKey || appScreen.hidden) return;
  switch (e.key) {
    case '/':
      e.preventDefault();
      busca.focus();
      break;
    case 'j':
      passaAgente(1);
      break;
    case 'k':
      passaAgente(-1);
      break;
    case 'f':
      if (state.selectedAgent) renderer?.select(state.selectedAgent);
      break;
    case 't':
      // sem isto o 't' cai na caixa de mensagem, que ganha o foco ao abrir
      e.preventDefault();
      if (state.selectedAgent) vivo.open(state.selectedAgent);
      break;
    case '?':
      mostraAjuda(!ajudaAberta());
      break;
    default:
      break;
  }
});

/** j e k: vai ao próximo ou ao anterior agente da lateral (na ordem da tela) e abre o painel dele. */
function passaAgente(passo: 1 | -1): void {
  const agentes = [...projetos.querySelectorAll<HTMLElement>('.agente')];
  if (!agentes.length) return;
  const atual = agentes.findIndex((el) => el.dataset.agent === state.selectedAgent);
  const proximo = atual < 0 ? (passo > 0 ? 0 : agentes.length - 1) : (atual + passo + agentes.length) % agentes.length;
  const id = agentes[proximo]?.dataset.agent ?? null;
  selectAgent(id, false);
  // render() refaz a lista: o item novo só existe depois dele
  [...projetos.querySelectorAll<HTMLElement>('.agente')].find((el) => el.dataset.agent === id)?.scrollIntoView({ block: 'nearest' });
}

// setas cima/baixo andam pela lista de agentes (principais e subagentes); Enter já é o clique do botão
projetos.addEventListener('keydown', (e) => {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const itens = [...projetos.querySelectorAll<HTMLElement>('.agente')];
  const atual = itens.indexOf(document.activeElement as HTMLElement);
  if (atual < 0) return;
  e.preventDefault();
  const proximo = Math.min(itens.length - 1, Math.max(0, atual + (e.key === 'ArrowDown' ? 1 : -1)));
  itens[proximo]?.focus();
});

// o feed também é navegável: Enter numa linha seleciona o agente dela
feedLista.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  (e.target as HTMLElement).closest<HTMLElement>('li[data-agent]')?.click();
});

filtros.addEventListener('click', (e) => {
  const p = (e.target as HTMLElement).closest<HTMLElement>('[data-provider]')?.dataset.provider as Provider | undefined;
  if (!p) return;
  if (state.hidden.has(p)) state.hidden.delete(p);
  else state.hidden.add(p);
  renderSidebar();
});

// o rascunho das perguntas acompanha o que a pessoa marca, para o repintar não apagar nada
// (o cartão do painel e o modal do celular usam os mesmos dois ouvintes)
function aoMudarPergunta(event: Event): void {
  const el = event.target as HTMLInputElement;
  const perm = el.closest<HTMLElement>('[data-perm]')?.dataset.perm;
  if (!perm || el.dataset.pergunta === undefined) return;
  const r = rascunhoDe(perm);
  const q = Number(el.dataset.pergunta);
  const opcao = Number(el.value);
  if (el.type === 'radio') {
    r.escolhas[q] = [opcao];
    // escolha única: a opção e o texto livre não valem juntos
    delete r.outro[q];
    const campo = el.closest('.pergunta-bloco')?.querySelector<HTMLInputElement>('[data-outro]');
    if (campo) campo.value = '';
    return;
  }
  const marcadas = new Set(r.escolhas[q] ?? []);
  if (el.checked) marcadas.add(opcao);
  else marcadas.delete(opcao);
  r.escolhas[q] = [...marcadas];
}

function aoDigitarPergunta(event: Event): void {
  const el = event.target as HTMLInputElement;
  const perm = el.closest<HTMLElement>('[data-perm]')?.dataset.perm;
  if (!perm) return;
  const r = rascunhoDe(perm);
  if (el.dataset.outro !== undefined) {
    const q = Number(el.dataset.outro);
    r.outro[q] = el.value;
    // escolha única: digitar no 'Outro' tira a opção marcada
    const bloco = el.closest('.pergunta-bloco');
    if (el.value && bloco?.querySelector('input[type="radio"]')) {
      bloco.querySelectorAll<HTMLInputElement>('input[type="radio"]').forEach((radio) => (radio.checked = false));
      r.escolhas[q] = [];
    }
  } else if (el.dataset.motivo !== undefined) {
    r.motivo = el.value;
  }
}

detalhe.addEventListener('change', aoMudarPergunta);
detalhe.addEventListener('input', aoDigitarPergunta);

for (const host of [projetos, feedLista, detalhe, toasts]) {
  host.addEventListener('click', async (event) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-close]')) {
      state.detail = null;
      state.selectedAgent = null;
      renderer?.select(null);
      render();
      return;
    }
    const acaoPergunta = target.closest<HTMLElement>('[data-pergunta-acao]');
    if (acaoPergunta) {
      await acaoDaPergunta(acaoPergunta);
      return;
    }
    // closest: o botão de sugestão tem texto dentro (<small>), o clique pode cair nele
    const botaoDecisao = target.closest<HTMLElement>('[data-decision]');
    const decision = botaoDecisao?.dataset.decision;
    const cartao = target.closest<HTMLElement>('[data-perm]');
    const permId = cartao?.getAttribute('data-perm');
    if ((decision === 'allow' || decision === 'deny') && permId) {
      const interromper = cartao?.querySelector<HTMLInputElement>('[data-interromper]')?.checked === true;
      await fetch(`/api/permissions/${encodeURIComponent(permId)}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpoDaDecisao(decision, { sugestao: sugestaoDoBotao(botaoDecisao?.dataset.sugestao), interromper })),
      });
      return;
    }
    const agentId = target.closest('[data-agent]')?.getAttribute('data-agent');
    if (agentId && !target.closest('.composer')) {
      target.closest('.toast')?.remove();
      selectAgent(agentId, true);
    }
  });
}

feedToggle.addEventListener('click', () => {
  state.feedOpen = !state.feedOpen;
  renderFeed();
});

btnPonte.addEventListener('click', () => {
  state.detail = state.detail === 'bridge' ? null : 'bridge';
  btnPonte.setAttribute('aria-pressed', String(state.detail === 'bridge'));
  renderTop();
  renderDetail();
});

// interruptores da Ponte: cada troca vai para POST /api/hooks (o servidor valida a trava do TOTP)
detalhe.addEventListener('change', async (event) => {
  const input = event.target as HTMLInputElement;
  const kind = input.dataset.ponte as 'approvals' | 'messages' | undefined;
  const provider = input.dataset.provider;
  if (!kind || !provider) return;
  const response = await fetch('/api/hooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ [kind]: { [provider]: input.checked } }),
  });
  const json = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  const erro = document.querySelector<HTMLElement>('#ponte-erro');
  if (!json.ok) {
    input.checked = !input.checked;
    if (erro) erro.textContent = t('ponte.erro', { erro: json.error ?? '?' });
  } else if (erro) {
    erro.textContent = '';
  }
});

montaDia({ agora: () => state.snapshot });

// o timelapse (B21) abre daqui: o painel do Meu dia pede pelo mesmo evento
window.addEventListener('agzos:painel', (evento) => {
  const detalhe = (evento as CustomEvent<{ tipo?: string; data?: string }>).detail;
  if (detalhe && typeof detalhe === 'object' && detalhe.tipo === 'timelapse') timelapseAbre(detalhe.data ?? chaveDoDia(new Date()));
});

/** Terminal: abre ou fecha o painel (botão da barra de ferramentas). */
function alternaTerminal(): void {
  // celular: o item Terminal (Mais) abre o terminal ao vivo em tela cheia, não o painel do escritório
  if (ehCelular()) return abreVivoDoCelular();
  if (state.detail === 'terminal') {
    state.detail = null;
    render();
  } else {
    abreTerminal();
  }
}


const BRIDGE_PROVIDERS: Provider[] = ['claude', 'codex', 'opencode'];

/** Painel da Ponte: aprovar e mandar mensagem, por CLI. */
function bridgePanel(snapshot: OfficeSnapshot): string {
  const f = snapshot.features;
  if (!f) return `<header class="det-cab"><span>${t('ponte.titulo')}</span></header>`;
  const row = (p: Provider) => {
    const label = escape(PROVIDER_LABELS[p] ?? p);
    const box = (kind: 'approvals' | 'messages', text: string) =>
      `<label class="interruptor"><input type="checkbox" data-ponte="${kind}" data-provider="${p}" ${f[kind][p] ? 'checked' : ''} ${kind === 'messages' && p === 'codex' ? 'disabled' : ''} /><span>${text}</span></label>`;
    const semFila = p === 'codex' ? `<small class="ponte-dica">${t('ponte.codex_fila')}</small>` : '';
    return `<div class="ponte-linha">${tagOf(p)}<b>${label}</b>${box('approvals', t('ponte.aprovar'))}${box('messages', t('ponte.mensagens'))}${semFila}</div>`;
  };
  return `<header class="det-cab"><span>${t('ponte.titulo')}</span><button class="fechar" data-close aria-label="${t('acao.fechar')}">✕</button></header>
    <p class="vazio">${t('ponte.explica')}</p>
    ${f.totp ? '' : `<p class="aviso">${t('ponte.totp')}</p>`}
    <div class="ponte">${BRIDGE_PROVIDERS.map(row).join('')}</div>
    <p class="vazio">${t('ponte.sessoes', { n: f.inbox.length })}</p>
    <p id="ponte-erro" class="erro" role="alert"></p>`;
}

/** Caixa de mensagem: só ativa quando a sessão está com o plugin de mensagens ligado. */
function composerHtml(agent: Agent, snapshot: OfficeSnapshot): string {
  const f = snapshot.features;
  const bridgeCli = (BRIDGE_PROVIDERS as string[]).includes(agent.provider);
  const off = bridgeCli && f && !f.messages[agent.provider];
  const noInbox = bridgeCli && f && f.messages[agent.provider] && !f.inbox.includes(agent.id);
  const dica = off ? t('det.desligado') : noInbox ? t('det.sem_caixa') : t('det.mandar_dica');
  const disabled = off || noInbox ? 'disabled' : '';
  return `<div class="composer"><textarea id="mensagem" rows="2" ${disabled} aria-describedby="mensagem-dica" placeholder="${escape(t('det.placeholder', { nome: agent.name }))}"></textarea><button id="enviar" ${disabled}>${t('det.enviar')}</button></div>${htmlDaSituacao(agent.id, t, 'mensagem-situacao', 'situacao-mensagem')}<p id="mensagem-dica" class="dica">${dica}</p>`;
}

/** Situação da última mensagem enviada (na fila, entregue, não entregue): aparece ao lado da caixa. */
let pararSituacao: (() => void) | undefined;
function acompanhaSituacao(agentId: string, id: string): void {
  pararSituacao?.();
  marcaEnvio(agentId, id);
  const repinta = () => pintaSituacao(document.querySelector<HTMLElement>('#mensagem-situacao'), agentId, t);
  repinta();
  pararSituacao = acompanhaMensagem(agentId, id, repinta);
}

/** Enter manda; Shift+Enter quebra a linha. O erro vai para o recado do painel, não para o botão. */
function bindComposer(agent: Agent): void {
  const input = document.querySelector<HTMLTextAreaElement>('#mensagem');
  const button = document.querySelector<HTMLButtonElement>('#enviar');
  if (!input || !button) return;
  const send = async () => {
    const text = input.value.trim();
    if (!text) return;
    const response = await fetch(`/api/agents/${encodeURIComponent(agent.id)}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    const json = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string; id?: string };
    if (json.ok) {
      input.value = '';
      if (json.id) acompanhaSituacao(agent.id, json.id);
    } else diz(json.error ?? t('erro.nao_deu'));
  };
  button.addEventListener('click', () => void send());
  input.addEventListener('keydown', (e) => {
    // isComposing: Enter que confirma acento em composição não manda a mensagem
    if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
    e.preventDefault();
    void send();
  });
}

/** Aviso no canto: alguém precisa de você. Um por agente/pedido. */
function toast(agent: Agent, text: string, key = `${agent.id}:waiting`): void {
  if (toasted.has(key)) return;
  toasted.add(key);
  const el = document.createElement('div');
  el.className = 'toast';
  el.dataset.agent = agent.id;
  const msg = t('aviso.msg', { nome: `<b>${escape(agent.name)}</b>`, texto: escape(text), projeto: escape(agent.project) });
  el.innerHTML = `<img class="avatar mini" src="${avatarUrl(lookOf(agent))}" alt="" width="24" height="24" /><span>${msg}</span><button class="fechar" aria-label="${t('acao.fechar')}">✕</button>`;
  el.querySelector('.fechar')?.addEventListener('click', (e) => {
    e.stopPropagation();
    el.remove();
  });
  toasts.append(el);
  setTimeout(() => el.remove(), 15_000);
  // depois de um minuto o mesmo agente pode avisar de novo
  setTimeout(() => toasted.delete(key), 60_000);
}

// ------------------------------------------------------------------ helpers

function lookOf(agent: Agent): Look {
  return agent.appearance ?? (CAST_BY_ID.get(agent.look) ?? castFor(agent.id)).look;
}

/** Lápis do painel (só admin, agente principal): abre o editor de personagem do projeto. */
async function editaPersonagem(agent: Agent): Promise<void> {
  const snapshot = state.snapshot;
  if (!snapshot || state.role !== 'admin') return;
  const lidos = (await fetch('/api/settings').then((r) => r.json()).catch(() => ({}))) as { ok?: boolean; settings?: OfficeSettings };
  if (!lidos.settings) return diz(t('erro.nao_deu'));
  const chave = `project:${agent.project}`;
  const automatico = castFor(agent.id);
  abreEditorPersonagem({
    titulo: t('ed.titulo_projeto', { projeto: agent.project }),
    atual: { look: lookOf(agent), nome: agent.name },
    sorteio: { look: automatico.look, nome: automatico.name },
    nomesEmUso: () =>
      nomesEmUso(
        { agentes: state.snapshot?.agents ?? [], personas: lidos.settings?.personas ?? {}, projetos: state.snapshot?.rooms.map((r) => r.project) ?? [] },
        { chave, agenteId: agent.id, projeto: agent.project },
      ),
    castId: lidos.settings.personas[chave]?.castId,
    salvar: async (persona) => {
      const resposta = (await fetch('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ personas: { [chave]: persona } }) })
        .then((r) => r.json())
        .catch(() => ({ ok: false, errors: [] }))) as { ok?: boolean; errors?: string[] };
      if (!resposta.ok) return resposta.errors?.join('; ') || t('erro.nao_deu');
      avisaSalvo(t('ed.salvo'));
      return undefined;
    },
  });
}

/** Aviso curto no canto (some sozinho): confirma que o personagem foi salvo. */
function avisaSalvo(texto: string): void {
  const aviso = document.createElement('div');
  aviso.className = 'toast';
  aviso.setAttribute('role', 'status');
  aviso.textContent = texto;
  toasts.append(aviso);
  setTimeout(() => aviso.remove(), 4000);
}

const avatars = new Map<string, string>();

/** Rosto do personagem (cabeça e ombros), como imagem para as listas. */
function avatarUrl(look: Look): string {
  const key = JSON.stringify(look);
  const hit = avatars.get(key);
  if (hit) return hit;
  const frame = characterFrame(look, 'down', 'idle');
  const out = document.createElement('canvas');
  out.width = 16;
  out.height = 16;
  const ctx = out.getContext('2d');
  if (!ctx) return '';
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(frame, 0, 0, 16, 16, 0, 0, 16, 16);
  const url = out.toDataURL();
  avatars.set(key, url);
  return url;
}

const ultimoHtml = new WeakMap<HTMLElement, string>();

/** Chave que reconhece o mesmo item depois de um innerHTML novo. */
function chaveDeFoco(el: HTMLElement): string | undefined {
  if (el.dataset.agent) return `agente:${el.dataset.agent}`;
  if (el.dataset.provider) return `filtro:${el.dataset.provider}`;
  if (el.dataset.decision) return `decisao:${el.closest('[data-perm]')?.getAttribute('data-perm')}:${el.dataset.decision}`;
  if (el.hasAttribute('data-close')) return 'fechar';
  if (el.dataset.acao) return `acao:${el.dataset.acao}`;
  if (el.id) return `id:${el.id}`;
  return undefined;
}

/**
 * Troca o conteúdo do container só quando o HTML mudou (devolve false se não mudou) e
 * devolve o foco de teclado ao mesmo item: o innerHTML tira o foco do elemento antigo.
 */
function trocaHtml(host: HTMLElement, html: string): boolean {
  if (ultimoHtml.get(host) === html) return false;
  ultimoHtml.set(host, html);
  const ativo = document.activeElement;
  const marca = ativo instanceof HTMLElement && host.contains(ativo) ? chaveDeFoco(ativo) : undefined;
  host.innerHTML = html;
  if (marca) {
    const novo = [...host.querySelectorAll<HTMLElement>('button, input, li[data-agent]')].find((el) => chaveDeFoco(el) === marca);
    novo?.focus({ preventScroll: true });
  }
  return true;
}

/** Primeira letra maiúscula: 'ocioso' vira 'Ocioso' (o início de uma frase). */
function capitaliza(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function shortPath(cwd: string): string {
  return cwd.replace(/^\/root/, '~');
}

function hora(at: number, seconds = false): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}${seconds ? `:${pad(d.getSeconds())}` : ''}`;
}

/** 'há 12s' até um minuto, depois 'há 3 min', desde o início do turno. */
function haQuanto(desde: number): string {
  const segundos = Math.max(0, Math.floor((Date.now() - desde) / 1000));
  return t('det.ha', { tempo: segundos < 60 ? `${segundos}s` : t('tempo.min', { n: Math.floor(segundos / 60) }) });
}

/** Cronômetro do turno: só quando o agente trabalha ou espera o shell (busySince). */
function cronometro(agent: Agent): string {
  if (!agent.busySince || (agent.status !== 'working' && agent.status !== 'shell')) return '';
  return `<small class="cronometro" data-desde="${agent.busySince}">${haQuanto(agent.busySince)}</small>`;
}

// troca só o texto dos cronômetros e dos 'há X': não refaz a lista nem o painel
setInterval(() => {
  for (const el of document.querySelectorAll<HTMLElement>('.cronometro[data-desde]')) el.textContent = haQuanto(Number(el.dataset.desde));
  for (const el of document.querySelectorAll<HTMLElement>('[data-ha]')) el.textContent = tempoRelativo(Number(el.dataset.ha));
}, 1000);

function statusLabel(status: Agent['status']): string {
  switch (status) {
    case 'working':
      return t('status.working');
    case 'thinking':
      return t('status.thinking');
    case 'waiting':
      return t('status.waiting');
    case 'shell':
      return t('status.shell');
    default:
      return t('status.idle');
  }
}

function escape(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

montaPwa();
void start();
